import {
  BED_JOINT_GAP_MM,
  BRICK_SPECS,
  BrickwallError,
  COORDINATE_QUANTUM_MM,
  applyBondPlan,
  closestPointOnSegment,
  closeShape,
  effectiveSize,
  formatMm,
  measure as makeMeasurement,
  planBondInShape,
  openingShapes,
  quantise,
  shapeBounds,
  shapeSegments,
  sketchId,
  snapSketchPoint,
  SKETCH_SNAP_LABELS,
  validateBondPlan,
  EMPTY_SKETCH,
  isEmptySketch,
} from '@bw/core';
import type {
  BondRequest,
  BrickId,
  BrickPattern,
  Bounds,
  CornerSnap,
  Measurement,
  PlacedBrick,
  LineKind,
  ShapeRole,
  SketchPoint,
  SketchSegment,
  SketchShape,
  SketchSnap,
  SnapLine,
  SnapResult,
  WallModel,
} from '@bw/core';
import type { CourseSpan } from '@bw/core';
import type { Renderer, Scene, SketchDrawMode } from '@bw/render-2d';
import { BUTTON_STEP, WHEEL_STEP, rotateHandleAt, screenToPlan } from '@bw/render-2d';
import { assembleBuilding, courseSpanFor, reachShortfall, wallReturns } from '@bw/building';
import type { BuildingModel, PlacedWall, Vec2 } from '@bw/building';
import type { ViewportState } from '@bw/render-2d';
import type { JoinerySpec, PlanSpan, RoofSpec, WallPlacement } from '@bw/document';
import { wallToPlan } from '@bw/building';
import {
  hitWall,
  moveWall,
  otherEnds,
  planAngleDeg,
  rotateWall,
  wallAt,
} from './plan.js';
import { COPY, plural, restingStatus } from '../copy.js';
import { activeModel, previewSize, useEditor } from '../state/store.js';
import type { EditorState } from '../state/store.js';
import { setVolatile } from '../state/volatile.js';
import { measurementText } from '@bw/render-2d';

/**
 * What is selected in the drawing: one closed shape, or one loose line.
 *
 * The three granularities the drawing offers are a shape, a line and a point.
 * A point is only ever selected for as long as it is being dragged, so it is
 * not one of these — it lives in the drag itself.
 */
export interface SketchTarget {
  readonly kind: 'shape' | 'segment';
  readonly id: string;
}

/** A vertex, addressed by its shape (or null for a loose line) and index. */
export interface VertexRef {
  readonly shapeId: string | null;
  readonly index: number;
  readonly point: SketchPoint;
}

/** What is under the pointer in the drawing. */
export type SketchHit =
  | { readonly kind: 'vertex'; readonly vertex: VertexRef; readonly target: SketchTarget }
  | { readonly kind: 'line'; readonly target: SketchTarget; readonly line: SnapLine };

/** How close, in pixels, the pointer has to be to grab drawn geometry. */
const SKETCH_GRAB_PX = 8;

const MARQUEE_THRESHOLD_PX = 5;
/** A shorter threshold than the marquee: a move should feel immediate. */
const MOVE_THRESHOLD_PX = 4;
const TEMPORARY_STATUS_MS = 3800;
/** Held arrow keys coalesce into one undo entry until this much quiet. */
const NUDGE_COALESCE_MS = 600;

type Gesture =
  | 'none'
  | 'pan'
  | 'marquee'
  | 'move'
  | 'envelope'
  | 'tool'
  | 'sketch-move'
  | 'plan-move'
  | 'plan-rotate';
type SelectionModifier = 'replace' | 'add' | 'toggle';

interface BuildPreviewState {
  readonly snap: SnapResult;
  readonly width: number;
  readonly height: number;
  readonly valid: boolean;
}

/**
 * All pointer interaction, outside React.
 *
 * Ephemeral state (pointer position, live snap, marquee rectangle) lives in
 * plain fields; the renderer pulls a scene from `scene()` each frame. Nothing
 * here calls `setState` on a per-move basis (design document B3).
 */
/** How many of a wall's two ends turn a bondable corner. */
function endsWithReturns(
  building: BuildingModel,
  perpendGapMm: number,
  wallId: string,
  spans: ReadonlyMap<string, PlanSpan>,
): number {
  const returns = wallReturns(building, perpendGapMm, undefined, spans).get(wallId);
  if (returns === undefined) return 0;
  return (returns.start === null ? 0 : 1) + (returns.end === null ? 0 : 1);
}

export class EditorController {
  #renderer: Renderer;
  #surface: HTMLElement | null = null;
  /**
   * Cached because `getBoundingClientRect` forces layout, and this runs on
   * every pointer event — sixty times a second through a drag.
   */
  #rect: { left: number; top: number } | null = null;
  #detached = false;
  #pointer: { x: number; y: number } | null = null;
  #gesture: Gesture = 'none';
  #panLast: { x: number; y: number } | null = null;
  #spaceHeld = false;

  #marqueePress: { x: number; y: number } | null = null;
  #marqueeCurrent: { x: number; y: number } | null = null;
  #marqueeBase: ReadonlySet<BrickId> = new Set();
  #marqueeModifier: SelectionModifier = 'replace';
  #marqueeMoved = false;

  #build: BuildPreviewState | null = null;
  #paste: { snap: SnapResult; valid: boolean } | null = null;
  #moveIds: ReadonlySet<BrickId> = new Set();
  #movePress: { x: number; y: number } | null = null;
  #moveOrigin: { x: number; y: number } | null = null;
  #moveOffset: { dx: number; dy: number } | null = null;
  #moveValid = false;
  #moveStarted = false;
  /**
   * Captured once when the drag begins. `patternFromIds` allocates and
   * compiles, so calling it per frame would put that cost on every pointer
   * move of a large selection.
   */
  #movePattern: BrickPattern | null = null;
  #moveBounds: Bounds | null = null;
  #moveRelation: SnapResult['relation'] = null;

  #bondRequest: Omit<BondRequest, 'envelope' | 'perpendGapMm'> = { bond: 'stretcher' };
  #drawMode: SketchDrawMode = 'polyline';
  #rectPress: { x: number; y: number } | null = null;
  /** The sketch vertex under a drag, addressed by shape (or null) and index. */
  #vertexDrag: VertexRef | null = null;
  #vertexPress: { x: number; y: number } | null = null;
  #vertexMoved = false;
  /** A whole shape or line being dragged, and where it has got to. */
  #sketchMove: {
    readonly target: SketchTarget;
    readonly press: { x: number; y: number };
    dx: number;
    dy: number;
    snap: SketchSnap | null;
    moved: boolean;
  } | null = null;

  #nudgeTimer: ReturnType<typeof setTimeout> | null = null;

  /** Live touch points, for the one-finger pan / two-finger pinch model (C7). */
  #touches = new Map<number, { x: number; y: number }>();
  #pinch: { distance: number; centre: { x: number; y: number } } | null = null;
  #touchStart: { x: number; y: number; at: number } | null = null;
  #longPressTimer: ReturnType<typeof setTimeout> | null = null;
  #onContextMenu: ((point: { x: number; y: number }) => void) | null = null;

  /** Where the keyboard is pointing when there is no mouse (design C8). */
  #keyboardCursor: { x: number; y: number } | null = null;

  // ---- sketch mode ----
  #draftPoints: SketchPoint[] = [];
  #draftCursor: SketchPoint | null = null;
  #draftClosing = false;
  #lineKind: LineKind = 'wall';
  #shapeRole: ShapeRole = 'wall';
  #sketchTarget: SketchTarget | null = null;
  /** What a press would grab, so the drawing can show it before it is grabbed. */
  #sketchHover: SketchHit | null = null;
  /** Where the point being placed or dragged has caught, for the marker. */
  #snapHint: SketchSnap | null = null;
  #onSketchChange: (() => void) | null = null;

  // ---- plan view (phase 2, M5) ----
  /**
   * The assembled building, rebuilt only when the document changes. Assembly
   * walks every wall, so doing it per frame would put that on every pointer
   * move; the revision it was built at is the cache key.
   */
  #building: BuildingModel | null = null;
  #buildingRevision = -1;
  #planDrag: {
    readonly wallId: string;
    readonly press: Vec2;
    readonly startPlacement: WallPlacement;
    readonly grabAngleDeg: number;
  } | null = null;
  #planPlacement: WallPlacement | null = null;
  #planMarks: readonly Vec2[] = [];
  #planMoved = false;
  /** Each view keeps its own pan and zoom across a tool switch. */
  #elevationView: ViewportState | null = null;
  #planView: ViewportState | null = null;

  #measureStart: CornerSnap | null = null;
  #measureEnd: CornerSnap | null = null;
  #measureHover: CornerSnap | null = null;

  #statusTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(renderer: Renderer) {
    this.#renderer = renderer;
    renderer.setScene(() => this.scene());
    renderer.onFrame((stats) =>
      setVolatile({ visibleBricks: stats.visible, zoom: renderer.viewport.zoomPercentText }),
    );
  }

  // ---------- lifecycle ----------

  attach(surface: HTMLElement): () => void {
    this.#surface = surface;
    const down = (event: PointerEvent) => this.onPointerDown(event);
    const move = (event: PointerEvent) => this.onPointerMove(event);
    const up = (event: PointerEvent) => this.onPointerUp(event);
    const leave = () => this.onPointerLeave();
    const wheel = (event: WheelEvent) => this.onWheel(event);
    const context = (event: MouseEvent) => event.preventDefault();
    const twice = (event: MouseEvent) => this.onDoubleClick(event);

    surface.addEventListener('pointerdown', down);
    surface.addEventListener('pointermove', move);
    surface.addEventListener('pointerup', up);
    surface.addEventListener('pointercancel', up);
    surface.addEventListener('pointerleave', leave);
    surface.addEventListener('wheel', wheel, { passive: false });
    surface.addEventListener('contextmenu', context);
    surface.addEventListener('dblclick', twice);

    this.#detached = false;
    this.measureSurface();
    const reposition = (): void => this.measureSurface();
    window.addEventListener('scroll', reposition, true);

    // Subscribed rather than called from the tool command: a tool also changes
    // when a brick key is pressed or a preview is rotated, and the plan's
    // viewport swap must not depend on which route was taken.
    let lastTool: string = useEditor.getState().tool;
    const unsubscribe = useEditor.subscribe((next) => {
      if (next.tool === lastTool) return;
      const previous = lastTool;
      lastTool = next.tool;
      this.syncViewForTool(previous, next.tool);
      // ...and so must the status line, which otherwise keeps describing the
      // tool you just left.
      this.restStatus();
    });

    return () => {
      this.#detached = true;
      // Timers must not outlive the controller: they mutate the store and the
      // renderer, both of which are gone by the time they would fire.
      if (this.#statusTimer !== null) clearTimeout(this.#statusTimer);
      if (this.#nudgeTimer !== null) clearTimeout(this.#nudgeTimer);
      if (this.#longPressTimer !== null) clearTimeout(this.#longPressTimer);
      this.#statusTimer = null;
      this.#nudgeTimer = null;
      this.#longPressTimer = null;
        this.#onSketchChange = null;
      this.#onContextMenu = null;
      unsubscribe();
      window.removeEventListener('scroll', reposition, true);
      surface.removeEventListener('pointerdown', down);
      surface.removeEventListener('pointermove', move);
      surface.removeEventListener('pointerup', up);
      surface.removeEventListener('pointercancel', up);
      surface.removeEventListener('pointerleave', leave);
      surface.removeEventListener('wheel', wheel);
      surface.removeEventListener('contextmenu', context);
      surface.removeEventListener('dblclick', twice);
      this.#surface = null;
      this.#rect = null;
    };
  }

  // ---------- state helpers ----------

  get #state(): EditorState {
    return useEditor.getState();
  }

  get #model(): WallModel {
    return activeModel(this.#state);
  }

  #localPoint(event: PointerEvent | WheelEvent | MouseEvent): { x: number; y: number } {
    if (this.#rect === null) this.measureSurface();
    const rect = this.#rect ?? { left: 0, top: 0 };
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  /** Re-read the surface's position. Called on resize and on scroll. */
  measureSurface(): void {
    const box = this.#surface?.getBoundingClientRect();
    this.#rect = box === undefined ? null : { left: box.left, top: box.top };
  }

  /** Mutate the document as one undoable step. */
  #mutate(run: () => unknown): void {
    this.#building = null;
    const state = this.#state;
    state.history.checkpoint(state.project);
    let changed: unknown = undefined;
    try {
      changed = run();
    } catch (error) {
      state.history.abandon();
      throw error;
    }
    if (changed === false) {
      state.history.abandon();
      return;
    }
    state.touch();
    this.#renderer.markWallDirty();
  }

  setStatus(text: string, temporary = false, announce = true): void {
    if (this.#statusTimer !== null) clearTimeout(this.#statusTimer);
    this.#statusTimer = null;
    this.#state.setStatus(text, !temporary, announce);
    if (temporary) {
      this.#statusTimer = setTimeout(() => {
        this.#statusTimer = null;
        if (!this.#detached) this.restStatus();
      }, TEMPORARY_STATUS_MS);
    }
  }

  /** Clause 16.1 — return the status line to the tool's resting text. */
  restStatus(): void {
    const state = this.#state;
    const [width, height] = previewSize(state);
    const measurement = this.#measurement;
    this.#state.setStatus(
      restingStatus(state.tool, {
        brickName: BRICK_SPECS[state.selectedKind].name,
        brickWidth: width,
        brickHeight: height,
        clipboardCount: state.clipboard?.bricks.length ?? 0,
        hasBricks: this.#model.size > 0,
        measureStage:
          this.#measureEnd !== null ? 'complete' : this.#measureStart !== null ? 'first' : 'none',
        measureSummary: measurement === null ? '' : summariseMeasurement(measurement),
        planStatus: this.#planStatus(),
      }),
      true,
    );
  }

  /** What the plan tool says when nothing is happening: the outline's state. */
  #planStatus(): string {
    const state = this.#state;
    if (state.tool !== 'plan') return COPY.planIdle;
    // Which wall is current is now on permanent display in the walls list and
    // the step bar, so the resting line spends itself on the outline instead.
    const footprint = this.building().footprint;
    const name = state.project.activeWall.name;
    if (footprint.closed) return COPY.planClosed(name, footprint.ring.length, footprint.areaMm2);
    if (footprint.openEnds.length > 0) return COPY.planOpen(name, footprint.openEnds.length);
    return COPY.planSelected(name);
  }

  // ---------- previews ----------

  get #measurement(): Measurement | null {
    if (this.#measureStart === null) return null;
    const end = this.#measureEnd ?? this.#measureHover;
    return end === null ? null : makeMeasurement(this.#measureStart, end);
  }

  #tolerance(bypass: boolean): number {
    const state = this.#state;
    return state.snapEnabled && !bypass ? this.#renderer.viewport.snapToleranceMm : 0;
  }

  #computeBuild(x: number, y: number, bypass: boolean): BuildPreviewState {
    const state = this.#state;
    const model = this.#model;
    const viewport = this.#renderer.viewport;
    const [width, height] = previewSize(state);
    const rawX = viewport.toWorldX(x) - width / 2;
    const rawY = viewport.toWorldY(y) - height / 2;
    const snap = model.snapPosition(rawX, rawY, width, height, this.#tolerance(bypass));
    return { snap, width, height, valid: model.canPlace(snap.x, snap.y, width, height) };
  }

  #computePaste(x: number, y: number, bypass: boolean): { snap: SnapResult; valid: boolean } | null {
    const state = this.#state;
    const pattern = state.clipboard;
    if (pattern === null) return null;
    const model = this.#model;
    const viewport = this.#renderer.viewport;
    const rawX = viewport.toWorldX(x) - pattern.width / 2;
    const rawY = viewport.toWorldY(y) - pattern.height / 2;
    const tolerance = this.#tolerance(bypass);
    if (tolerance > 0) {
      const snap = model.snapPatternPosition(pattern, rawX, rawY, tolerance);
      // Pattern snapping validates before returning, so a snapped result needs
      // no second group collision pass (clause 8.5).
      if (snap.relation !== null) return { snap, valid: true };
      return { snap, valid: model.canPlacePattern(pattern, snap.x, snap.y) };
    }
    const snap = model.snapPatternPosition(pattern, rawX, rawY, 0);
    return { snap, valid: model.canPlacePattern(pattern, snap.x, snap.y) };
  }

  /** Recompute whatever the current tool previews, then redraw the overlay. */
  refreshPreview(bypass = false): void {
    const state = this.#state;
    const pointer = this.#pointer;
    this.#build = null;
    this.#paste = null;
    if (pointer !== null && this.#panLast === null) {
      if (state.tool === 'build') {
        this.#build = this.#computeBuild(pointer.x, pointer.y, bypass);
      } else if (state.tool === 'paste') {
        this.#paste = this.#computePaste(pointer.x, pointer.y, bypass);
      } else if (state.tool === 'measure') {
        this.#measureHover = this.#cornerAt(pointer.x, pointer.y);
      } else if (state.tool === 'sketch') {
        this.#updateSketchCursor(pointer, bypass);
      }
    }
    if (state.tool !== 'sketch') this.#sketchHover = null;
    if (state.tool !== 'measure') this.#measureHover = null;
    this.#publishReadouts();
    this.#renderer.markOverlayDirty();
  }

  #cornerAt(x: number, y: number): CornerSnap | null {
    const viewport = this.#renderer.viewport;
    return this.#model.nearestCorner(viewport.toWorldX(x), viewport.toWorldY(y));
  }

  #publishReadouts(): void {
    const state = this.#state;
    const pointer = this.#pointer;
    const viewport = this.#renderer.viewport;
    let coordinate = 'X —   Y —';
    if (pointer !== null) {
      if (state.tool === 'measure' && this.#measureHover !== null) {
        coordinate = `Corner X ${formatMm(this.#measureHover.x)}   Y ${formatMm(this.#measureHover.y)} mm`;
      } else if (state.tool === 'measure') {
        coordinate = 'Corner X —   Y —';
      } else {
        coordinate = `X ${formatMm(viewport.toWorldX(pointer.x))}   Y ${formatMm(viewport.toWorldY(pointer.y))} mm`;
      }
    }
    const measurement = this.#measurement;
    let measurementText_: string = COPY.measureNothing;
    if (state.tool === 'measure') {
      if (measurement !== null) measurementText_ = measurementText(measurement).join('\n');
      else if (this.#measureStart !== null) {
        measurementText_ = `Start X ${formatMm(this.#measureStart.x)}, Y ${formatMm(this.#measureStart.y)} mm\nClick the second brick corner`;
      } else measurementText_ = this.#model.size > 0 ? COPY.measureFirst : COPY.measureNoBricks;
    }
    setVolatile({
      coordinate,
      measurement: measurementText_,
      snapRelation:
        this.#build?.snap.relation ??
        this.#paste?.snap.relation ??
        (this.#snapHint === null ? null : SKETCH_SNAP_LABELS[this.#snapHint.kind]),
    });
  }

  clearMeasurement(): void {
    this.#measureStart = null;
    this.#measureEnd = null;
    this.#measureHover = null;
  }

  // ---------- pointer ----------

  onPointerDown(event: PointerEvent): void {
    (event.currentTarget as HTMLElement | null)?.focus?.();
    const point = this.#localPoint(event);
    this.#pointer = point;
    this.#surface?.setPointerCapture?.(event.pointerId);

    if (event.pointerType === 'touch') {
      this.#touches.set(event.pointerId, point);
      if (this.#touches.size === 1) {
        this.#touchStart = { ...point, at: Date.now() };
        this.#armLongPress(point);
        // A single finger pans; the tool acts on a tap (design document C7).
        this.#gesture = 'pan';
        this.#panLast = point;
        this.#build = null;
        this.#paste = null;
        this.#renderer.markOverlayDirty();
      } else if (this.#touches.size === 2) {
        this.#cancelLongPress();
        this.#touchStart = null;
        this.#gesture = 'none';
        this.#panLast = null;
        this.#pinch = this.#pinchState();
      }
      return;
    }

    // Middle or right button, the pan tool, or a held Space: pan (clause 10.2).
    if (event.button === 1 || event.button === 2 || this.#spaceHeld || this.#state.tool === 'pan') {
      this.#gesture = 'pan';
      this.#panLast = point;
      this.#build = null;
      this.#paste = null;
      this.#renderer.markOverlayDirty();
      return;
    }
    if (event.button !== 0) return;

    if (this.#state.tool === 'plan') {
      this.#planPointerDown(point, event);
      return;
    }

    const state = this.#state;
    // With no run in progress the sketch tool edits what is already drawn.
    // Taking hold of an existing corner takes precedence over starting a new
    // run, so a corner can be corrected without redrawing the shape (F18).
    if (state.tool === 'sketch' && this.#draftPoints.length === 0) {
      const grabbed = this.#sketchHitAt(point);
      if (grabbed?.kind === 'vertex') {
        this.#gesture = 'move';
        this.#vertexDrag = grabbed.vertex;
        this.#vertexPress = point;
        // Not a drag yet: a click on a corner still starts a new run there,
        // which is how an outline is continued from an existing point.
        this.#vertexMoved = false;
        this.#renderer.markOverlayDirty();
        return;
      }
      if (grabbed?.kind === 'line') {
        // A line is the whole shape's handle: dragging it moves the shape,
        // and a click that never travelled has selected it, which is what
        // Delete and the shape list then act on.
        this.#gesture = 'sketch-move';
        this.#beginSketchMove(grabbed.target, point);
        this.#sketchHover = null;
        this.#renderer.markOverlayDirty();
        return;
      }
    }

    if (state.tool === 'sketch' && this.#drawMode === 'rectangle') {
      this.#gesture = 'envelope';
      this.#rectPress = point;
      this.#draftPoints = [];
      this.#renderer.markOverlayDirty();
      return;
    }

    if (state.tool === 'select') {
      const viewport = this.#renderer.viewport;
      const hit = this.#model.hitTest(viewport.toWorldX(point.x), viewport.toWorldY(point.y));
      const plain = !event.shiftKey && !event.ctrlKey && !event.metaKey;
      if (hit !== undefined && plain) {
        // Pressing an unselected brick selects it and begins a move in the
        // same gesture — the standard behaviour (design document C3).
        const selection = state.selected.has(hit.id) ? state.selected : new Set([hit.id]);
        if (selection !== state.selected) state.setSelected(selection);
        this.#gesture = 'move';
        this.#moveIds = new Set(selection);
        this.#movePress = point;
        this.#moveOrigin = point;
        this.#moveOffset = { dx: 0, dy: 0 };
        this.#moveStarted = false;
        this.#moveValid = true;
        this.#movePattern = null;
        this.#moveBounds = null;
        this.#renderer.markWallDirty();
        return;
      }
      this.#gesture = 'marquee';
      this.#marqueePress = point;
      this.#marqueeCurrent = point;
      this.#marqueeBase = new Set(state.selected);
      this.#marqueeModifier = event.ctrlKey || event.metaKey ? 'toggle' : event.shiftKey ? 'add' : 'replace';
      this.#marqueeMoved = false;
      return;
    }

    this.#gesture = 'tool';
    this.runToolAt(point, event.altKey);
  }

  /** The tool's click action, shared by the mouse and by a touch tap. */
  runToolAt(point: { x: number; y: number }, bypassSnap: boolean): void {
    switch (this.#state.tool) {
      case 'build':
        this.#layBrick(point, bypassSnap);
        break;
      case 'paint':
        this.#paintAt(point);
        break;
      case 'paste':
        this.#pasteAt(point, bypassSnap);
        break;
      case 'measure':
        this.#measureAt(point);
        break;
      case 'sketch':
        this.#sketchClick(point, bypassSnap);
        break;
      case 'plan': {
        // Missing every wall leaves the current one active rather than
        // clearing it: one wall is always the wall you are working on.
        const hit = wallAt(this.building(), this.#planPoint(point));
        if (hit !== null && this.#state.project.setActiveWall(hit.id)) {
          this.#state.setSelected(new Set());
          this.#state.touch();
          this.setStatus(COPY.planSelected(hit.name), false, false);
        }
        this.#renderer.markWallDirty();
        break;
      }
      case 'select': {
        const viewport = this.#renderer.viewport;
        const brick = this.#model.hitTest(viewport.toWorldX(point.x), viewport.toWorldY(point.y));
        const next = brick === undefined ? new Set<BrickId>() : new Set([brick.id]);
        this.#state.setSelected(next);
        this.setStatus(next.size > 0 ? COPY.selected(next.size) : COPY.selectionCleared);
        this.#renderer.markWallDirty();
        break;
      }
      default:
        break;
    }
  }

  // ---------- touch (design document C7) ----------

  onContextMenu(callback: ((point: { x: number; y: number }) => void) | null): void {
    this.#onContextMenu = callback;
  }

  #armLongPress(point: { x: number; y: number }): void {
    this.#cancelLongPress();
    this.#longPressTimer = setTimeout(() => {
      this.#longPressTimer = null;
      this.#gesture = 'none';
      this.#panLast = null;
      this.#touchStart = null;
      this.#onContextMenu?.(point);
    }, 500);
  }

  #cancelLongPress(): void {
    if (this.#longPressTimer !== null) clearTimeout(this.#longPressTimer);
    this.#longPressTimer = null;
  }

  #pinchState(): { distance: number; centre: { x: number; y: number } } | null {
    const points = [...this.#touches.values()];
    if (points.length < 2) return null;
    const [a, b] = points as [{ x: number; y: number }, { x: number; y: number }];
    return {
      distance: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
      centre: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
    };
  }

  /** Two fingers pinch to zoom and drag to pan, about the gesture's centre. */
  #updatePinch(): void {
    const next = this.#pinchState();
    const previous = this.#pinch;
    if (next === null) return;
    if (previous === null) {
      this.#pinch = next;
      return;
    }
    this.#renderer.viewport.panBy(next.centre.x - previous.centre.x, next.centre.y - previous.centre.y);
    this.#renderer.viewport.zoomAt(next.distance / previous.distance, next.centre.x, next.centre.y);
    this.#pinch = next;
    this.#renderer.markWallDirty();
  }

  /** Selection helpers the touch context menu offers in place of modifiers. */
  addToSelectionAt(point: { x: number; y: number }): void {
    const viewport = this.#renderer.viewport;
    const brick = this.#model.hitTest(viewport.toWorldX(point.x), viewport.toWorldY(point.y));
    if (brick === undefined) return;
    const next = new Set(this.#state.selected);
    next.add(brick.id);
    this.#state.setSelected(next);
    this.setStatus(COPY.selected(next.size));
    this.#renderer.markWallDirty();
  }

  toggleSelectionAt(point: { x: number; y: number }): void {
    const viewport = this.#renderer.viewport;
    const brick = this.#model.hitTest(viewport.toWorldX(point.x), viewport.toWorldY(point.y));
    if (brick === undefined) return;
    const next = new Set(this.#state.selected);
    if (next.has(brick.id)) next.delete(brick.id);
    else next.add(brick.id);
    this.#state.setSelected(next);
    this.setStatus(next.size > 0 ? COPY.selected(next.size) : COPY.selectionCleared);
    this.#renderer.markWallDirty();
  }

  onPointerMove(event: PointerEvent): void {
    const point = this.#localPoint(event);
    this.#pointer = point;

    if (event.pointerType === 'touch') {
      if (!this.#touches.has(event.pointerId)) return;
      this.#touches.set(event.pointerId, point);
      if (
        this.#touchStart !== null &&
        Math.hypot(point.x - this.#touchStart.x, point.y - this.#touchStart.y) > 10
      ) {
        this.#cancelLongPress();
      }
      if (this.#touches.size >= 2) {
        this.#updatePinch();
        return;
      }
      if (this.#gesture === 'pan' && this.#panLast !== null) {
        this.#renderer.viewport.panBy(point.x - this.#panLast.x, point.y - this.#panLast.y);
        this.#panLast = point;
        this.#renderer.markWallDirty();
      }
      return;
    }

    if (this.#gesture === 'pan' && this.#panLast !== null) {
      this.#renderer.viewport.panBy(point.x - this.#panLast.x, point.y - this.#panLast.y);
      this.#panLast = point;
      this.#renderer.markWallDirty();
      this.#publishReadouts();
      return;
    }

    if (this.#gesture === 'plan-move' || this.#gesture === 'plan-rotate') {
      this.#planPointerMove(point, event);
      return;
    }

    if (this.#gesture === 'move' && this.#vertexDrag !== null) {
      this.#dragVertex(point, event.altKey);
      return;
    }

    if (this.#gesture === 'sketch-move') {
      this.#updateSketchMove(point, event.altKey);
      return;
    }

    if (this.#gesture === 'move' && this.#movePress !== null) {
      this.#updateMove(point, event);
      return;
    }

    if (this.#gesture === 'envelope' && this.#rectPress !== null) {
      this.#updateRectangle(point);
      return;
    }

    if (this.#gesture === 'marquee' && this.#marqueePress !== null) {
      this.#marqueeCurrent = point;
      if (
        Math.hypot(point.x - this.#marqueePress.x, point.y - this.#marqueePress.y) >=
        MARQUEE_THRESHOLD_PX
      ) {
        this.#marqueeMoved = true;
      }
      this.#renderer.markOverlayDirty();
      this.#publishReadouts();
      return;
    }

    // Drag-painting: hold the button and sweep a run (design document C2).
    if (this.#gesture === 'tool' && this.#state.tool === 'paint' && event.buttons === 1) {
      this.#paintAt(point, true);
      return;
    }

    this.refreshPreview(event.altKey);
  }

  onPointerUp(event: PointerEvent): void {
    const point = this.#localPoint(event);
    this.#pointer = point;
    this.#surface?.releasePointerCapture?.(event.pointerId);

    if (event.pointerType === 'touch') {
      this.#touches.delete(event.pointerId);
      this.#cancelLongPress();
      if (this.#touches.size < 2) this.#pinch = null;
      const start = this.#touchStart;
      this.#touchStart = null;
      this.#gesture = 'none';
      this.#panLast = null;
      // A tap that never travelled runs the tool; a drag was a pan.
      const tapped =
        start !== null &&
        this.#touches.size === 0 &&
        Math.hypot(point.x - start.x, point.y - start.y) <= 10 &&
        Date.now() - start.at < 700;
      if (tapped) this.runToolAt(point, false);
      else this.refreshPreview();
      return;
    }

    if (this.#gesture === 'pan') {
      this.#panLast = null;
      this.#gesture = 'none';
      this.refreshPreview(event.altKey);
      return;
    }

    if (this.#gesture === 'plan-move' || this.#gesture === 'plan-rotate') {
      this.#planPointerUp();
      return;
    }

    if (this.#gesture === 'move' && this.#vertexDrag !== null) {
      this.#commitVertex(point, event.altKey);
      this.#gesture = 'none';
      return;
    }

    if (this.#gesture === 'sketch-move') {
      this.#commitSketchMove();
      this.#gesture = 'none';
      return;
    }

    if (this.#gesture === 'move') {
      this.#commitMove();
      this.#gesture = 'none';
      this.#renderer.markWallDirty();
      return;
    }

    if (this.#gesture === 'envelope') {
      this.#rectPress = null;
      this.#gesture = 'none';
      if (this.#draftPoints.length === 4) this.closeSketchShape();
      else this.setStatus(COPY.sketchRectangle, true);
      return;
    }

    if (this.#gesture === 'marquee' && this.#marqueePress !== null) {
      this.#commitSelection(point);
    }
    this.#gesture = 'none';
    this.#marqueePress = null;
    this.#marqueeCurrent = null;
    this.#marqueeMoved = false;
    this.#renderer.markOverlayDirty();
  }

  /**
   * A second click on a drawn line adds a corner to it.
   *
   * Safe to hang off a double click because a click on a line selects rather
   * than draws: the two clicks that got here left nothing behind to undo. A
   * double click anywhere a run is being drawn is two points of that run and
   * nothing else, which is why this refuses to act on one.
   */
  onDoubleClick(event: MouseEvent): void {
    if (this.#state.tool !== 'sketch' || this.#draftPoints.length > 0) return;
    const point = this.#localPoint(event);
    if (this.#lineAt(point) === null) return;
    event.preventDefault();
    this.addSketchPoint(point, event.altKey);
  }

  onPointerLeave(): void {
    if (this.#gesture !== 'none') return;
    this.#pointer = null;
    this.#build = null;
    this.#paste = null;
    this.#measureHover = null;
    this.#sketchHover = null;
    this.#snapHint = null;
    this.#publishReadouts();
    this.#renderer.markOverlayDirty();
  }

  onWheel(event: WheelEvent): void {
    event.preventDefault();
    const point = this.#localPoint(event);
    const factor = event.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
    if (this.#renderer.viewport.zoomAt(factor, point.x, point.y)) {
      this.#renderer.markWallDirty();
      this.refreshPreview(event.altKey);
    }
  }

  // ---------- tool actions ----------

  #layBrick(point: { x: number; y: number }, bypass: boolean): void {
    const state = this.#state;
    const model = this.#model;
    const preview = this.#computeBuild(point.x, point.y, bypass);
    this.#build = preview;
    if (!preview.valid) {
      this.setStatus(COPY.cannotLay(model.perpendGapMm), true);
      this.#renderer.markOverlayDirty();
      return;
    }
    let laid: PlacedBrick | null = null;
    this.#mutate(() => {
      laid = model.addBrick(state.selectedKind, preview.snap.x, preview.snap.y, {
        rotation: state.previewRotation,
        materialId: state.activeMaterialId,
      });
    });
    if (laid !== null) {
      const brick = laid as PlacedBrick;
      state.setSelected(new Set([brick.id]));
      this.setStatus(
        COPY.laid(BRICK_SPECS[brick.kind].name, brick.x, brick.y, preview.snap.relation),
        true,
        false,
      );
    }
    this.refreshPreview(bypass);
  }

  #paintAt(point: { x: number; y: number }, quiet = false): void {
    const state = this.#state;
    const model = this.#model;
    const viewport = this.#renderer.viewport;
    const brick = model.hitTest(viewport.toWorldX(point.x), viewport.toWorldY(point.y));
    if (brick === undefined) {
      if (!quiet) this.setStatus(COPY.paintMiss, true);
      return;
    }
    if (brick.materialId === state.activeMaterialId) {
      state.setSelected(new Set([brick.id]));
      return;
    }
    this.#mutate(() => model.setMaterial(brick.id, state.activeMaterialId));
    state.setSelected(new Set([brick.id]));
    if (!quiet) this.setStatus(COPY.painted(BRICK_SPECS[brick.kind].name), true, false);
  }

  #pasteAt(point: { x: number; y: number }, bypass: boolean): void {
    const state = this.#state;
    const model = this.#model;
    const pattern = state.clipboard;
    if (pattern === null) {
      this.setStatus(COPY.nothingToPaste, true);
      return;
    }
    const preview = this.#computePaste(point.x, point.y, bypass);
    this.#paste = preview;
    if (preview === null || !preview.valid) {
      this.setStatus(COPY.cannotPaste(model.perpendGapMm), true);
      this.#renderer.markOverlayDirty();
      return;
    }
    let placed: PlacedBrick[] = [];
    this.#mutate(() => {
      placed = model.placePattern(pattern, preview.snap.x, preview.snap.y);
    });
    state.setSelected(new Set(placed.map((brick) => brick.id)));
    this.setStatus(COPY.pasted(placed.length, preview.snap.relation), true);
    this.refreshPreview(bypass);
  }

  #measureAt(point: { x: number; y: number }): void {
    const corner = this.#cornerAt(point.x, point.y);
    if (corner === null) {
      this.setStatus(COPY.measureNoCorners, true);
      return;
    }
    if (this.#measureStart === null || this.#measureEnd !== null) {
      this.#measureStart = corner;
      this.#measureEnd = null;
      this.#measureHover = null;
      this.setStatus(COPY.measureFirstFixed(corner.x, corner.y), true);
    } else {
      this.#measureEnd = corner;
      this.setStatus(`Measurement · ${summariseMeasurement(makeMeasurement(this.#measureStart, corner))} · Click to start another`, true);
    }
    this.#publishReadouts();
    this.#renderer.markOverlayDirty();
  }

  // ---------- move and nudge (design document C3) ----------

  #updateMove(point: { x: number; y: number }, event: PointerEvent): void {
    const press = this.#movePress;
    if (press === null) return;
    const model = this.#model;
    if (!this.#moveStarted) {
      if (Math.hypot(point.x - press.x, point.y - press.y) < MOVE_THRESHOLD_PX) return;
      this.#moveStarted = true;
      try {
        this.#movePattern = model.patternFromIds(this.#moveIds);
        this.#moveBounds = model.boundsForIds(this.#moveIds);
      } catch {
        this.#resetMove();
        this.#gesture = 'none';
        return;
      }
    }
    const viewport = this.#renderer.viewport;
    let dx = viewport.toWorldX(point.x) - viewport.toWorldX(press.x);
    let dy = viewport.toWorldY(point.y) - viewport.toWorldY(press.y);
    // Shift constrains to the dominant axis, as everywhere else.
    if (event.shiftKey) {
      if (Math.abs(dx) >= Math.abs(dy)) dy = 0;
      else dx = 0;
    }

    const bounds = this.#moveBounds;
    const pattern = this.#movePattern;
    if (bounds === null || pattern === null) return;
    const tolerance = this.#tolerance(event.altKey);
    if (tolerance > 0) {
      // A move is a paste of the selection with the selection excluded from
      // collision, so it inherits the whole snap ladder for free.
      const snap = model.snapPatternPosition(
        pattern,
        bounds.minX + dx,
        bounds.minY + dy,
        tolerance,
        this.#moveIds,
      );
      dx = snap.x - bounds.minX;
      dy = snap.y - bounds.minY;
      this.#moveRelation = snap.relation;
    } else {
      dx = quantise(dx);
      dy = quantise(dy);
      this.#moveRelation = null;
    }

    this.#moveOffset = { dx, dy };
    this.#moveValid = model.canMoveBricks(this.#moveIds, dx, dy);
    this.#moveOrigin = point;
    this.#renderer.markOverlayDirty();
    this.#publishReadouts();
  }

  #commitMove(): void {
    const offset = this.#moveOffset;
    const ids = this.#moveIds;
    const started = this.#moveStarted;
    const valid = this.#moveValid;
    const relation = this.#moveRelation;
    this.#resetMove();
    if (!started || offset === null) {
      // The press never became a drag, so it was an ordinary click: report the
      // selection it made, exactly as the marquee path does.
      const count = this.#state.selected.size;
      this.setStatus(count > 0 ? COPY.selected(count) : COPY.selectionCleared);
      return;
    }
    if (offset.dx === 0 && offset.dy === 0) return;
    if (!valid) {
      this.setStatus(COPY.cannotMove(this.#model.perpendGapMm), true);
      return;
    }
    let moved = false;
    this.#mutate(() => {
      moved = this.#model.moveBricks(ids, offset.dx, offset.dy);
      return moved;
    });
    if (moved) this.setStatus(COPY.moved(ids.size, relation), true);
  }

  #resetMove(): void {
    this.#moveIds = new Set();
    this.#movePress = null;
    this.#moveOrigin = null;
    this.#moveOffset = null;
    this.#moveStarted = false;
    this.#moveValid = false;
    this.#moveRelation = null;
    this.#movePattern = null;
    this.#moveBounds = null;
  }

  /** Arrow-key nudge with the step ladder from design document C3. */
  nudge(direction: 'left' | 'right' | 'up' | 'down', modifiers: {
    readonly shift?: boolean;
    readonly alt?: boolean;
    readonly ctrl?: boolean;
  } = {}): void {
    const state = this.#state;
    const model = this.#model;
    const ids = [...state.selected].filter((id) => model.get(id) !== undefined);
    if (ids.length === 0) {
      this.setStatus(COPY.nothingToDelete.replace('deleting', 'nudging'), true);
      return;
    }
    const horizontal = direction === 'left' || direction === 'right';
    const sign = direction === 'left' || direction === 'down' ? -1 : 1;

    let step: number;
    if (modifiers.ctrl) {
      const first = model.get(ids[0] as BrickId);
      step = horizontal
        ? (first?.width ?? 215) / 2 + model.perpendGapMm / 2 // half bond
        : (first?.height ?? 65) + BED_JOINT_GAP_MM; // one course
    } else if (modifiers.alt) {
      step = COORDINATE_QUANTUM_MM;
    } else if (modifiers.shift) {
      step = 10;
    } else {
      step = 1;
    }

    const dx = horizontal ? sign * step : 0;
    const dy = horizontal ? 0 : sign * step;
    if (!model.canMoveBricks(ids, dx, dy)) {
      this.setStatus(COPY.nudgeRefused(model.perpendGapMm), true);
      this.#renderer.markWallDirty();
      return;
    }

    // Held arrows coalesce, so holding a key does not fill the history.
    if (this.#nudgeTimer === null) {
      state.history.checkpoint(state.project);
    } else {
      clearTimeout(this.#nudgeTimer);
    }
    this.#nudgeTimer = setTimeout(() => {
      this.#nudgeTimer = null;
    }, NUDGE_COALESCE_MS);

    model.moveBricks(ids, dx, dy);
    state.touch();
    this.#renderer.markWallDirty();
    this.setStatus(COPY.moved(ids.length, null), true, false);
  }

  /** Close the current nudge run, so the next one starts a new undo entry. */
  endNudgeRun(): void {
    if (this.#nudgeTimer !== null) {
      clearTimeout(this.#nudgeTimer);
      this.#nudgeTimer = null;
    }
  }

  // ---------- sketch mode ----------

  onSketchChange(callback: (() => void) | null): void {
    this.#onSketchChange = callback;
  }

  get lineKind(): LineKind {
    return this.#lineKind;
  }

  get shapeRole(): ShapeRole {
    return this.#shapeRole;
  }

  get draftPointCount(): number {
    return this.#draftPoints.length;
  }

  /** Where the point being placed or dragged has caught, if anywhere. */
  get snapHint(): SketchSnap | null {
    return this.#snapHint;
  }

  get selectedShapeId(): string | null {
    return this.#sketchTarget?.kind === 'shape' ? this.#sketchTarget.id : null;
  }

  /** What is selected in the drawing, shape or loose line alike. */
  get sketchSelection(): SketchTarget | null {
    return this.#sketchTarget;
  }

  setLineKind(kind: LineKind): void {
    this.#lineKind = kind;
    this.#onSketchChange?.();
    this.#renderer.markOverlayDirty();
  }

  setShapeRole(role: ShapeRole): void {
    this.#shapeRole = role;
    this.#onSketchChange?.();
  }

  selectShape(id: string | null): void {
    this.selectSketch(id === null ? null : { kind: 'shape', id });
  }

  /** Select drawn geometry — a closed shape, or one loose line. */
  selectSketch(target: SketchTarget | null): void {
    const same =
      target?.kind === this.#sketchTarget?.kind && target?.id === this.#sketchTarget?.id;
    this.#sketchTarget = target;
    if (!same) this.#onSketchChange?.();
    this.#renderer.markOverlayDirty();
  }

  /**
   * Everything a sketch point may catch on, near where it is being put.
   *
   * Bricks are gathered from the index by the tolerance box rather than by
   * asking for the single nearest corner: an edge is as much a thing to snap
   * to as a corner is, and a course of brickwork under the pointer offers
   * several of both.
   *
   * `without` names geometry that must not be a target — a vertex cannot snap
   * to itself, and a shape being dragged cannot catch its own edges.
   */
  #snapSources(
    worldX: number,
    worldY: number,
    tolerance: number,
    options: { readonly without?: SketchTarget | null; readonly from?: SketchPoint | null } = {},
  ): Parameters<typeof snapSketchPoint>[3] {
    const brickwork = this.#brickSnapSources(worldX, worldY, tolerance);
    return {
      points: this.#draftPoints,
      lines: this.#drawnLines(options.without ?? null),
      brickCorners: brickwork.corners,
      brickEdges: brickwork.edges,
      from: options.from ?? null,
    };
  }

  /**
   * Every drawn line, less whatever is being moved.
   *
   * Split out from the sources because it does not depend on where the point
   * is, and a whole-shape move asks for the sources once per point of the
   * shape being moved: rebuilding a fifty-sided outline's edges fifty times a
   * frame is the difference between a drag that keeps up and one that does not.
   */
  #drawnLines(without: SketchTarget | null): SnapLine[] {
    const sketch = this.#model.sketch;
    const lines: SnapLine[] = [];
    for (const segment of sketch.segments) {
      if (without?.kind === 'segment' && without.id === segment.id) continue;
      lines.push(segment);
    }
    for (const shape of sketch.shapes) {
      if (without?.kind === 'shape' && without.id === shape.id) continue;
      lines.push(...shapeSegments(shape));
    }
    return lines;
  }

  /** The corners and edges of the brickwork within reach of a point. */
  #brickSnapSources(
    worldX: number,
    worldY: number,
    tolerance: number,
  ): { corners: SketchPoint[]; edges: SnapLine[] } {
    const corners: SketchPoint[] = [];
    const edges: SnapLine[] = [];
    if (this.#model.size === 0) return { corners, edges };
    for (const brick of this.#model.bricksInRect(
      worldX - tolerance, worldY - tolerance, worldX + tolerance, worldY + tolerance,
    )) {
      const { x, y, right, top } = brick;
      corners.push({ x, y }, { x: right, y }, { x: right, y: top }, { x, y: top });
      edges.push(
        { a: { x, y }, b: { x: right, y }, id: `${brick.id}:b` },
        { a: { x: right, y }, b: { x: right, y: top }, id: `${brick.id}:r` },
        { a: { x: right, y: top }, b: { x, y: top }, id: `${brick.id}:t` },
        { a: { x, y: top }, b: { x, y }, id: `${brick.id}:l` },
      );
    }
    return { corners, edges };
  }

  /**
   * Where a sketch point lands, and what it caught.
   *
   * The snap is kept so the drawing can mark it: a point that has locked onto
   * a brick corner and a point that merely happens to be near one look the
   * same on screen, and the difference is the whole reason for snapping.
   */
  #snapPointAt(
    point: { x: number; y: number },
    bypass: boolean,
    options: { readonly without?: SketchTarget | null; readonly from?: SketchPoint | null } = {},
  ): { point: SketchPoint; snap: SketchSnap | null } {
    const viewport = this.#renderer.viewport;
    const worldX = viewport.toWorldX(point.x);
    const worldY = viewport.toWorldY(point.y);
    const tolerance = this.#tolerance(bypass);
    if (tolerance <= 0) return { point: { x: quantise(worldX), y: quantise(worldY) }, snap: null };
    const snap = snapSketchPoint(worldX, worldY, tolerance, this.#snapSources(worldX, worldY, tolerance, options));
    if (snap === null) return { point: { x: quantise(worldX), y: quantise(worldY) }, snap: null };
    return { point: { x: snap.x, y: snap.y }, snap };
  }

  /** Where a sketch point lands, with the run being drawn as the axis datum. */
  #sketchSnap(point: { x: number; y: number }, bypass = false): SketchPoint {
    const last = this.#draftPoints[this.#draftPoints.length - 1] ?? null;
    const result = this.#snapPointAt(point, bypass, { from: last });
    this.#snapHint = result.snap;
    return result.point;
  }

  /** The sketch vertex under a screen point, within the snap tolerance. */
  #vertexAt(point: { x: number; y: number }): VertexRef | null {
    const viewport = this.#renderer.viewport;
    const worldX = viewport.toWorldX(point.x);
    const worldY = viewport.toWorldY(point.y);
    // The same reach as a line, and deliberately smaller than the snap
    // tolerance: the ring between the two is where a click starts a new run
    // AT a corner rather than taking hold of it.
    const tolerance = SKETCH_GRAB_PX / viewport.scale;
    let best: VertexRef | null = null;
    let bestDistance = tolerance;
    const consider = (shapeId: string | null, index: number, p: SketchPoint): void => {
      const distance = Math.hypot(p.x - worldX, p.y - worldY);
      if (distance <= bestDistance) {
        bestDistance = distance;
        best = { shapeId, index, point: p };
      }
    };
    const sketch = this.#model.sketch;
    for (const shape of sketch.shapes) {
      shape.points.forEach((p, index) => consider(shape.id, index, p));
    }
    sketch.segments.forEach((segment, index) => {
      consider(null, index * 2, segment.a);
      consider(null, index * 2 + 1, segment.b);
    });
    return best;
  }

  /** Which shape or loose line a vertex belongs to. */
  #ownerOf(vertex: VertexRef): SketchTarget | null {
    if (vertex.shapeId !== null) return { kind: 'shape', id: vertex.shapeId };
    const segment = this.#model.sketch.segments[Math.floor(vertex.index / 2)];
    return segment === undefined ? null : { kind: 'segment', id: segment.id };
  }

  /**
   * What a press at this point would grab: a vertex first, then a line.
   *
   * A vertex wins inside its own reach even when a line runs through it,
   * because the smaller thing is the harder one to hit and the one that was
   * aimed at.
   */
  #sketchHitAt(point: { x: number; y: number }): SketchHit | null {
    const vertex = this.#vertexAt(point);
    if (vertex !== null) {
      const target = this.#ownerOf(vertex);
      if (target !== null) return { kind: 'vertex', vertex, target };
    }
    return this.#lineAt(point);
  }

  /** The drawn line under a screen point, and what it belongs to. */
  #lineAt(point: { x: number; y: number }): (SketchHit & { kind: 'line' }) | null {
    const viewport = this.#renderer.viewport;
    const worldX = viewport.toWorldX(point.x);
    const worldY = viewport.toWorldY(point.y);
    const reach = SKETCH_GRAB_PX / viewport.scale;
    let best: (SketchHit & { kind: 'line' }) | null = null;
    let bestDistance = reach;
    const consider = (target: SketchTarget, line: SnapLine): void => {
      const foot = closestPointOnSegment(line.a, line.b, worldX, worldY);
      const distance = Math.hypot(foot.x - worldX, foot.y - worldY);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = { kind: 'line', target, line };
      }
    };
    const sketch = this.#model.sketch;
    for (const segment of sketch.segments) consider({ kind: 'segment', id: segment.id }, segment);
    for (const shape of sketch.shapes) {
      for (const edge of shapeSegments(shape)) consider({ kind: 'shape', id: shape.id }, edge);
    }
    return best;
  }

  /** Every point of a shape or a loose line, in order. */
  #pointsOf(target: SketchTarget): readonly SketchPoint[] {
    const sketch = this.#model.sketch;
    if (target.kind === 'shape') {
      return sketch.shapes.find((shape) => shape.id === target.id)?.points ?? [];
    }
    const segment = sketch.segments.find((candidate) => candidate.id === target.id);
    return segment === undefined ? [] : [segment.a, segment.b];
  }

  #dragVertex(point: { x: number; y: number }, bypass = false): void {
    const drag = this.#vertexDrag;
    const press = this.#vertexPress;
    if (drag === null || press === null) return;
    if (!this.#vertexMoved) {
      if (Math.hypot(point.x - press.x, point.y - press.y) < MOVE_THRESHOLD_PX) return;
      this.#vertexMoved = true;
    }
    // Without itself as a target: a point cannot be caught by where it was.
    const owner = this.#ownerOf(drag);
    const moved = this.#snapPointAt(point, bypass, { without: owner });
    this.#snapHint = moved.snap;
    this.#vertexDrag = { ...drag, point: moved.point };
    this.#publishReadouts();
    this.#renderer.markOverlayDirty();
  }

  #commitVertex(point: { x: number; y: number }, bypass = false): void {
    const drag = this.#vertexDrag;
    const moved = this.#vertexMoved;
    this.#vertexDrag = null;
    this.#vertexPress = null;
    this.#vertexMoved = false;
    this.#snapHint = null;
    if (drag === null) return;
    if (!moved) {
      // A click, not a drag. In the sketch tool that starts a new run at this
      // very vertex, which is how an outline is continued from a point that
      // already exists; everywhere else it is a selection and nothing more.
      if (this.#state.tool === 'sketch') this.#sketchClick(point, bypass);
      else this.selectSketch(this.#ownerOf(drag));
      return;
    }
    const sketch = this.#model.sketch;
    const target = drag.point;
    let changed = false;
    if (drag.shapeId === null) {
      const segmentIndex = Math.floor(drag.index / 2);
      const end = drag.index % 2 === 0 ? 'a' : 'b';
      const segment = sketch.segments[segmentIndex];
      if (segment === undefined) return;
      if (segment[end].x === target.x && segment[end].y === target.y) return;
      const segments = sketch.segments.map((candidate, index) =>
        index === segmentIndex ? { ...candidate, [end]: target } : candidate,
      );
      this.#mutate(() => this.#model.setSketch({ ...sketch, segments }));
      changed = true;
    } else {
      const shape = sketch.shapes.find((candidate) => candidate.id === drag.shapeId);
      if (shape === undefined) return;
      const current = shape.points[drag.index];
      if (current === undefined || (current.x === target.x && current.y === target.y)) return;
      const points = shape.points.map((p, index) => (index === drag.index ? target : p));
      let replacement: SketchShape;
      try {
        replacement = { ...shape, points: closeShape(points, shape.role, shape.kind).points };
      } catch (error) {
        // Dragging a corner across the shape would fold it over itself.
        this.setStatus(error instanceof BrickwallError ? error.message : String(error), true);
        this.#renderer.markOverlayDirty();
        return;
      }
      const shapes = sketch.shapes.map((candidate) =>
        candidate.id === shape.id ? replacement : candidate,
      );
      this.#mutate(() => this.#model.setSketch({ ...sketch, shapes }));
      changed = true;
    }
    if (changed) {
      this.setStatus(COPY.sketchMovedPoint(target.x, target.y), true);
      this.#onSketchChange?.();
      this.#renderer.markWallDirty();
    }
  }

  // ---------- moving what is drawn ----------

  /**
   * Start dragging a whole shape or a whole line.
   *
   * Selecting it at the same moment is deliberate: the thing about to move has
   * to be visibly the thing that was grabbed, and a press that turns out to be
   * a click has then done something useful anyway.
   */
  #beginSketchMove(target: SketchTarget, press: { x: number; y: number }): void {
    this.#sketchMove = { target, press, dx: 0, dy: 0, snap: null, moved: false };
    this.selectSketch(target);
    this.setStatus(COPY.sketchSelected(target.kind, this.#pointsOf(target).length), false, false);
  }

  #updateSketchMove(point: { x: number; y: number }, bypass = false): void {
    const move = this.#sketchMove;
    if (move === null) return;
    if (!move.moved) {
      if (Math.hypot(point.x - move.press.x, point.y - move.press.y) < MOVE_THRESHOLD_PX) return;
      move.moved = true;
    }
    const viewport = this.#renderer.viewport;
    const rawDx = viewport.toWorldX(point.x) - viewport.toWorldX(move.press.x);
    const rawDy = viewport.toWorldY(point.y) - viewport.toWorldY(move.press.y);
    const settled = this.#snapTranslation(move.target, rawDx, rawDy, bypass);
    move.dx = settled.dx;
    move.dy = settled.dy;
    move.snap = settled.snap;
    this.#snapHint = settled.snap;
    this.#publishReadouts();
    this.#renderer.markOverlayDirty();
  }

  /**
   * A translation, corrected so that one of the moved points lands on
   * something.
   *
   * Every point of the thing being moved is offered the translation and asked
   * what it would catch; the nearest catch of the lot wins, and its correction
   * is applied to the whole. Snapping the pointer instead would align nothing
   * to anything — the point under the hand is rarely a corner — whereas this
   * is what lets a window be dragged until one of its own corners locks onto a
   * course of brickwork.
   *
   * Ranked across points by reach alone. Which kind of thing was caught has
   * already decided the winner within each point, and between two points it is
   * the nearer lock that the hand is aiming at.
   */
  #snapTranslation(
    target: SketchTarget,
    rawDx: number,
    rawDy: number,
    bypass: boolean,
  ): { dx: number; dy: number; snap: SketchSnap | null } {
    const tolerance = this.#tolerance(bypass);
    const points = this.#pointsOf(target);
    if (tolerance <= 0 || points.length === 0) {
      return { dx: quantise(rawDx), dy: quantise(rawDy), snap: null };
    }
    let best: SketchSnap | null = null;
    let bestReach = Infinity;
    let fixX = 0;
    let fixY = 0;
    const lines = this.#drawnLines(target);
    for (const p of points) {
      const x = p.x + rawDx;
      const y = p.y + rawDy;
      const brickwork = this.#brickSnapSources(x, y, tolerance);
      const snap = snapSketchPoint(x, y, tolerance, {
        lines,
        brickCorners: brickwork.corners,
        brickEdges: brickwork.edges,
      });
      if (snap === null) continue;
      const reach = Math.hypot(snap.x - x, snap.y - y);
      if (reach < bestReach) {
        bestReach = reach;
        best = snap;
        fixX = snap.x - x;
        fixY = snap.y - y;
      }
    }
    return { dx: quantise(rawDx + fixX), dy: quantise(rawDy + fixY), snap: best };
  }

  #commitSketchMove(): void {
    const move = this.#sketchMove;
    this.#sketchMove = null;
    this.#snapHint = null;
    if (move === null) return;
    if (!move.moved || (move.dx === 0 && move.dy === 0)) {
      this.#renderer.markOverlayDirty();
      return;
    }
    const shift = (p: SketchPoint): SketchPoint => ({
      x: quantise(p.x + move.dx),
      y: quantise(p.y + move.dy),
    });
    const sketch = this.#model.sketch;
    // A translation cannot make a ring cross itself, so nothing here needs the
    // validation that moving a single corner does.
    const next =
      move.target.kind === 'shape'
        ? {
            ...sketch,
            shapes: sketch.shapes.map((shape) =>
              shape.id === move.target.id ? { ...shape, points: shape.points.map(shift) } : shape,
            ),
          }
        : {
            ...sketch,
            segments: sketch.segments.map((segment) =>
              segment.id === move.target.id
                ? { ...segment, a: shift(segment.a), b: shift(segment.b) }
                : segment,
            ),
          };
    this.#mutate(() => this.#model.setSketch(next));
    this.setStatus(COPY.sketchMoved(move.target.kind, move.dx, move.dy), true);
    this.#onSketchChange?.();
    this.#renderer.markWallDirty();
  }

  /**
   * Add a corner to a drawn line, where the pointer is.
   *
   * A shape gains a point in its ring; a loose line is split into two, which
   * is the same thing said about an open run. The new point takes a snap only
   * when the snap is on the line it is being added to — a corner added to a
   * line that then sits off it is not a corner, it is a kink.
   */
  addSketchPoint(point: { x: number; y: number }, bypass = false): boolean {
    const hit = this.#lineAt(point);
    if (hit === null) {
      this.setStatus(COPY.sketchNoLine, true);
      return false;
    }
    const viewport = this.#renderer.viewport;
    const worldX = viewport.toWorldX(point.x);
    const worldY = viewport.toWorldY(point.y);
    const foot = closestPointOnSegment(hit.line.a, hit.line.b, worldX, worldY);
    const snapped = this.#snapPointAt(point, bypass);
    const onLine = closestPointOnSegment(hit.line.a, hit.line.b, snapped.point.x, snapped.point.y);
    // Half a quantum, so a snap that is meant to be on the line survives being
    // put on the coordinate lattice.
    const strays =
      Math.hypot(onLine.x - snapped.point.x, onLine.y - snapped.point.y) >
      COORDINATE_QUANTUM_MM;
    const at: SketchPoint = strays
      ? { x: quantise(foot.x), y: quantise(foot.y) }
      : snapped.point;

    const sketch = this.#model.sketch;
    if (hit.target.kind === 'shape') {
      const shape = sketch.shapes.find((candidate) => candidate.id === hit.target.id);
      // `shapeSegments` names each edge `<shape>:<index>`, which is the index
      // the new point goes in after.
      const edge = Number.parseInt((hit.line.id ?? '').split(':')[1] ?? '', 10);
      if (shape === undefined || !Number.isInteger(edge)) return false;
      const points = [...shape.points];
      points.splice(edge + 1, 0, at);
      let replacement: SketchShape;
      try {
        replacement = { ...shape, points: closeShape(points, shape.role, shape.kind).points };
      } catch {
        // Adding a point on top of one of its neighbours changes nothing.
        return false;
      }
      if (replacement.points.length === shape.points.length) return false;
      this.#mutate(() =>
        this.#model.setSketch({
          ...sketch,
          shapes: sketch.shapes.map((candidate) =>
            candidate.id === shape.id ? replacement : candidate,
          ),
        }),
      );
    } else {
      const index = sketch.segments.findIndex((candidate) => candidate.id === hit.target.id);
      const segment = sketch.segments[index];
      if (segment === undefined) return false;
      const near = (p: SketchPoint): boolean =>
        Math.abs(p.x - at.x) < COORDINATE_QUANTUM_MM && Math.abs(p.y - at.y) < COORDINATE_QUANTUM_MM;
      if (near(segment.a) || near(segment.b)) return false;
      const split: SketchSegment[] = [
        { id: sketchId('g'), kind: segment.kind, a: segment.a, b: at },
        { id: sketchId('g'), kind: segment.kind, a: at, b: segment.b },
      ];
      const segments = [...sketch.segments];
      segments.splice(index, 1, ...split);
      this.#mutate(() => this.#model.setSketch({ ...sketch, segments }));
      // The line it was on is gone, so what was selected has to go with it.
      if (this.#sketchTarget?.id === segment.id) this.#sketchTarget = null;
    }
    this.setStatus(COPY.sketchAddedPoint(at.x, at.y), true);
    this.#onSketchChange?.();
    this.#renderer.markWallDirty();
    return true;
  }

  /** Whether Delete has drawn geometry to act on. */
  get hasSketchSelection(): boolean {
    return this.#sketchTarget !== null;
  }

  /** Delete whatever is selected in the drawing. */
  deleteSketchSelection(): boolean {
    const target = this.#sketchTarget;
    if (target === null) return false;
    if (target.kind === 'shape') {
      this.deleteShape(target.id);
      this.setStatus(COPY.sketchDeletedShape, true);
      return true;
    }
    const sketch = this.#model.sketch;
    if (!sketch.segments.some((segment) => segment.id === target.id)) return false;
    this.#mutate(() =>
      this.#model.setSketch({
        ...sketch,
        segments: sketch.segments.filter((segment) => segment.id !== target.id),
      }),
    );
    this.#sketchTarget = null;
    this.setStatus(COPY.sketchDeletedLine, true);
    this.#onSketchChange?.();
    this.#renderer.markWallDirty();
    return true;
  }

  #sketchClick(point: { x: number; y: number }, bypass = false): void {
    const snapped = this.#sketchSnap(point, bypass);
    const first = this.#draftPoints[0];
    const wouldClose =
      first !== undefined &&
      this.#draftPoints.length >= 3 &&
      Math.abs(snapped.x - first.x) < 1e-6 &&
      Math.abs(snapped.y - first.y) < 1e-6;
    if (wouldClose) {
      this.closeSketchShape();
      return;
    }
    // Drawing is not editing: what was selected stops being selected, so that
    // Delete cannot take a shape away in the middle of drawing the next one.
    if (this.#draftPoints.length === 0 && this.#sketchTarget !== null) {
      this.#sketchTarget = null;
      this.#onSketchChange?.();
    }
    this.#draftPoints.push(snapped);
    const previous = this.#draftPoints[this.#draftPoints.length - 2];
    this.setStatus(
      previous === undefined
        ? COPY.sketchStart
        : COPY.sketchPoint(this.#draftPoints.length, Math.hypot(snapped.x - previous.x, snapped.y - previous.y)),
    );
    this.#onSketchChange?.();
    this.#renderer.markOverlayDirty();
  }

  #updateSketchCursor(point: { x: number; y: number }, bypass = false): void {
    if (this.#draftPoints.length === 0) {
      this.#draftCursor = null;
      this.#draftClosing = false;
      // Nothing is being drawn, but something may still be about to be
      // grabbed, and the drawing says which before it is.
      this.#sketchHover = this.#sketchHitAt(point);
      this.#snapHint = null;
      return;
    }
    this.#sketchHover = null;
    const snapped = this.#sketchSnap(point, bypass);
    const first = this.#draftPoints[0] as SketchPoint;
    this.#draftClosing =
      this.#draftPoints.length >= 3 &&
      Math.abs(snapped.x - first.x) < 1e-6 &&
      Math.abs(snapped.y - first.y) < 1e-6;
    this.#draftCursor = snapped;
    this.#renderer.markOverlayDirty();
  }

  /** Close the drawn chain into a shape with the panel's role. */
  closeSketchShape(): boolean {
    if (this.#draftPoints.length < 3) {
      this.setStatus(COPY.sketchTooFew, true);
      return false;
    }
    const points = [...this.#draftPoints];
    let shape: SketchShape;
    try {
      shape = closeShape(points, this.#shapeRole, this.#lineKind);
    } catch (error) {
      this.setStatus(error instanceof BrickwallError ? error.message : String(error), true);
      return false;
    }
    this.#mutate(() => {
      const sketch = this.#model.sketch;
      this.#model.setSketch({ ...sketch, shapes: [...sketch.shapes, shape] });
    });
    this.#draftPoints = [];
    this.#draftCursor = null;
    this.#draftClosing = false;
    this.#sketchTarget = { kind: 'shape', id: shape.id };
    this.setStatus(COPY.sketchClosed(shape.role, shape.points.length), true);
    this.#onSketchChange?.();
    this.#renderer.markWallDirty();
    return true;
  }

  /** End the chain without closing it, keeping the lines as guides. */
  finishSketchLine(): void {
    if (this.#draftPoints.length < 2) {
      this.#draftPoints = [];
      this.#draftCursor = null;
      this.#onSketchChange?.();
      this.#renderer.markOverlayDirty();
      return;
    }
    const points = [...this.#draftPoints];
    const kind = this.#lineKind;
    this.#mutate(() => {
      const sketch = this.#model.sketch;
      const segments = [...sketch.segments];
      for (let i = 0; i < points.length - 1; i++) {
        segments.push({
          id: sketchId('g'),
          kind,
          a: points[i] as SketchPoint,
          b: points[i + 1] as SketchPoint,
        });
      }
      this.#model.setSketch({ ...sketch, segments });
    });
    this.setStatus(COPY.sketchOpen(points.length - 1), true);
    this.#draftPoints = [];
    this.#draftCursor = null;
    this.#onSketchChange?.();
    this.#renderer.markWallDirty();
  }

  undoSketchPoint(): void {
    this.#draftPoints.pop();
    this.#onSketchChange?.();
    this.#renderer.markOverlayDirty();
  }

  setShapeRoleFor(id: string, role: ShapeRole): void {
    this.#mutate(() => {
      const sketch = this.#model.sketch;
      this.#model.setSketch({
        ...sketch,
        shapes: sketch.shapes.map((shape) => (shape.id === id ? { ...shape, role } : shape)),
      });
    });
    this.#onSketchChange?.();
    this.#renderer.markWallDirty();
  }

  deleteShape(id: string): void {
    this.#mutate(() => {
      const sketch = this.#model.sketch;
      this.#model.setSketch({ ...sketch, shapes: sketch.shapes.filter((shape) => shape.id !== id) });
    });
    if (this.#sketchTarget?.id === id) this.#sketchTarget = null;
    this.#onSketchChange?.();
    this.#renderer.markWallDirty();
  }

  clearSketch(): void {
    this.#mutate(() => this.#model.setSketch(EMPTY_SKETCH));
    this.#draftPoints = [];
    this.#draftCursor = null;
    this.#sketchTarget = null;
    this.setStatus(COPY.sketchCleared, true);
    this.#onSketchChange?.();
    this.#renderer.markWallDirty();
  }

  /**
   * How this wall's courses are set out, when it meets another at a corner.
   *
   * Null when it does not, which is the ordinary case and leaves the bond
   * generator laying to the plain outline exactly as before.
   */
  #setOut(shape: SketchShape): { readonly courseSpan: CourseSpan; readonly span: PlanSpan } | null {
    const state = this.#state;
    if (!state.bondCorners) return null;
    const wall = state.project.activeWall;
    // The outline being filled IS this wall's design line. Saying so up front
    // is what lets a wall drawn but not yet laid be seen to have a corner —
    // otherwise it has no length in plan and every first fill misses its
    // corners.
    const bounds = shapeBounds(shape);
    const span: PlanSpan = { minX: bounds.minX, maxX: bounds.maxX };
    const returns = wallReturns(
      this.building(),
      this.#model.perpendGapMm,
      undefined,
      new Map([[wall.id, span]]),
    ).get(wall.id);
    if (returns === undefined || (returns.start === null && returns.end === null)) return null;
    return { courseSpan: courseSpanFor(returns, wall.placement.origin.y), span };
  }

  /** How many corners the wall being drawn meets another wall at. */
  activeWallCorners(): number {
    const model = this.#model;
    const wallId = this.#state.project.activeWallId;
    // Count against the outline being drawn, for the same reason the fill sets
    // out against it: a wall with nothing laid in it yet still has corners.
    // Counted per shape and not over all of them at once: a fill sets out
    // against the one outline it is filling, so a union of two separate wall
    // shapes could promise a corner that no single fill would find.
    const drawn = model.sketch.shapes.filter((shape) => shape.role === 'wall');
    const building = this.building();
    let most = 0;
    for (const shape of drawn.length > 0 ? drawn : []) {
      const bounds = shapeBounds(shape);
      if (bounds.maxX <= bounds.minX) continue;
      const spans = new Map<string, PlanSpan>([
        [wallId, { minX: bounds.minX, maxX: bounds.maxX }],
      ]);
      most = Math.max(most, endsWithReturns(building, model.perpendGapMm, wallId, spans));
    }
    return drawn.length > 0 ? most : endsWithReturns(building, model.perpendGapMm, wallId, new Map());
  }

  /** Automatically lay a bond inside a closed wall shape (sketch mode). */
  fillShape(id: string): void {
    const model = this.#model;
    const shape = model.sketch.shapes.find((candidate) => candidate.id === id);
    if (shape === undefined || shape.role !== 'wall') return;
    const setOut = this.#setOut(shape);
    const plan = planBondInShape(shape, openingShapes(model.sketch), {
      bond: this.#bondRequest.bond,
      perpendGapMm: model.perpendGapMm,
      closer: this.#bondRequest.closer ?? 'header',
      materialId: this.#state.activeMaterialId,
      ...(setOut === null ? {} : { courseSpan: setOut.courseSpan }),
    });
    let placed = 0;
    let skipped = 0;
    this.#mutate(() => {
      const result = applyBondPlan(model, plan);
      placed = result.placed.length;
      skipped = result.skipped;
      // Courses that run through a corner reach past the design line, so pin
      // the line: the plan should keep reading the wall people drew, not the
      // brickwork that ties it to its neighbour. Cleared again when a fill
      // does not bond, or a wall filled once with corners and again without
      // would keep the length it had the first time.
      if (placed > 0) this.#state.project.activeWall.span = setOut?.span ?? null;
      return placed > 0;
    });
    if (placed === 0) {
      this.setStatus(COPY.cannotLay(model.perpendGapMm), true);
      return;
    }
    // Measured from the bricks rather than taken from the plan: a shape fill
    // lays with an overhang and trims, so the plan's own shortfall is always
    // spent on the unit hanging past the end.
    const shortfall = setOut === null ? 0 : reachShortfall(plan.bricks, setOut.courseSpan);
    this.setStatus(
      shortfall > 0
        ? `${COPY.sketchFilled(placed, skipped)} ${COPY.cornerShortfall(shortfall)}`
        : COPY.sketchFilled(placed, skipped),
      true,
    );
    this.#onSketchChange?.();
    this.refreshPreview();
  }

  /** How many bricks a fill would lay, for the panel's preview. */
  previewFill(id: string): { placed: number; skipped: number } | null {
    const model = this.#model;
    const shape = model.sketch.shapes.find((candidate) => candidate.id === id);
    if (shape === undefined || shape.role !== 'wall') return null;
    const setOut = this.#setOut(shape);
    const plan = planBondInShape(shape, openingShapes(model.sketch), {
      bond: this.#bondRequest.bond,
      perpendGapMm: model.perpendGapMm,
      closer: this.#bondRequest.closer ?? 'header',
      ...(setOut === null ? {} : { courseSpan: setOut.courseSpan }),
    });
    const split = validateBondPlan(model, plan);
    return { placed: split.placeable.length, skipped: split.clashing.length };
  }

  // ---------- keyboard operation (design document C8) ----------

  /**
   * A canvas is opaque to assistive technology, so every drawing operation has
   * to be reachable without a pointer. The keyboard cursor is the pointer's
   * stand-in: arrows move it by the C3 nudge steps and Enter lays a brick.
   */
  get keyboardCursor(): { x: number; y: number } | null {
    return this.#keyboardCursor;
  }

  #stepFor(horizontal: boolean, modifiers: { shift?: boolean; alt?: boolean; ctrl?: boolean }): number {
    if (modifiers.ctrl) {
      const [width, height] = previewSize(this.#state);
      return horizontal ? width / 2 + this.#model.perpendGapMm / 2 : height + BED_JOINT_GAP_MM;
    }
    if (modifiers.alt) return COORDINATE_QUANTUM_MM;
    if (modifiers.shift) return 10;
    return 1;
  }

  /** Move the keyboard cursor, seeding it at the viewport centre first. */
  moveKeyboardCursor(
    direction: 'left' | 'right' | 'up' | 'down',
    modifiers: { shift?: boolean; alt?: boolean; ctrl?: boolean } = {},
  ): void {
    const viewport = this.#renderer.viewport;
    if (this.#keyboardCursor === null) {
      const bounds = this.#model.bounds();
      this.#keyboardCursor =
        bounds === null
          ? { x: quantise(viewport.toWorldX(viewport.width / 2)), y: 0 }
          : { x: quantise(bounds.minX), y: quantise(bounds.maxY + BED_JOINT_GAP_MM) };
    } else {
      const horizontal = direction === 'left' || direction === 'right';
      const sign = direction === 'left' || direction === 'down' ? -1 : 1;
      const step = this.#stepFor(horizontal, modifiers) * sign;
      this.#keyboardCursor = {
        x: quantise(this.#keyboardCursor.x + (horizontal ? step : 0)),
        y: quantise(this.#keyboardCursor.y + (horizontal ? 0 : step)),
      };
    }
    const cursor = this.#keyboardCursor;
    const [width, height] = previewSize(this.#state);
    // Keep the cursor on screen, so keyboard work is not driven blind.
    const view = viewport.visibleBounds(-40);
    if (cursor.x < view.minX || cursor.x + width > view.maxX || cursor.y < view.minY || cursor.y + height > view.maxY) {
      viewport.panBy(
        viewport.toScreenX(view.minX + (view.maxX - view.minX) / 2) - viewport.toScreenX(cursor.x + width / 2),
        viewport.toScreenY(view.minY + (view.maxY - view.minY) / 2) - viewport.toScreenY(cursor.y + height / 2),
      );
    }
    this.#refreshKeyboardPreview();
    this.setStatus(
      `Cursor at X ${formatMm(cursor.x)}, Y ${formatMm(cursor.y)} mm. Enter lays a ${BRICK_SPECS[this.#state.selectedKind].name}.`,
    );
  }

  #refreshKeyboardPreview(): void {
    const cursor = this.#keyboardCursor;
    if (cursor === null || this.#state.tool !== 'build') {
      this.#build = null;
    } else {
      const model = this.#model;
      const [width, height] = previewSize(this.#state);
      const snap = model.snapPosition(cursor.x, cursor.y, width, height, 0);
      this.#build = { snap, width, height, valid: model.canPlace(snap.x, snap.y, width, height) };
    }
    this.#renderer.markWallDirty();
    this.#renderer.markOverlayDirty();
  }

  /** Lay a brick at the keyboard cursor. */
  layAtKeyboardCursor(): void {
    const cursor = this.#keyboardCursor;
    if (cursor === null || this.#state.tool !== 'build') return;
    const model = this.#model;
    const state = this.#state;
    const [width, height] = previewSize(state);
    if (!model.canPlace(cursor.x, cursor.y, width, height)) {
      this.setStatus(COPY.cannotLay(model.perpendGapMm), true);
      return;
    }
    let laid: PlacedBrick | null = null;
    this.#mutate(() => {
      laid = model.addBrick(state.selectedKind, cursor.x, cursor.y, {
        rotation: state.previewRotation,
        materialId: state.activeMaterialId,
      });
    });
    if (laid !== null) {
      const brick = laid as PlacedBrick;
      state.setSelected(new Set([brick.id]));
      this.setStatus(COPY.laid(BRICK_SPECS[brick.kind].name, brick.x, brick.y, null), true);
      // Advance along the course, so a run can be laid by repeating Enter.
      this.#keyboardCursor = { x: quantise(brick.right + model.perpendGapMm), y: brick.y };
    }
    this.#refreshKeyboardPreview();
  }

  clearKeyboardCursor(): void {
    this.#keyboardCursor = null;
    this.#build = null;
    this.#renderer.markOverlayDirty();
  }

  /** Bricks in reading order: course by course, left to right. */
  bricksInCourseOrder(): readonly PlacedBrick[] {
    return [...this.#model.bricks].sort((a, b) => (a.y === b.y ? a.x - b.x : a.y - b.y));
  }

  /**
   * Step the selection through the wall in course order. Returns false at the
   * end of the run so focus can leave the canvas — a keyboard trap would be an
   * accessibility failure, not a feature.
   */
  cycleSelection(backwards: boolean): boolean {
    const ordered = this.bricksInCourseOrder();
    if (ordered.length === 0) return false;
    const selected = this.#state.selected;
    const current = ordered.findIndex((brick) => selected.has(brick.id));
    const next = current === -1 ? (backwards ? ordered.length - 1 : 0) : current + (backwards ? -1 : 1);
    if (next < 0 || next >= ordered.length) return false;
    const brick = ordered[next] as PlacedBrick;
    this.#state.setSelected(new Set([brick.id]));
    this.#renderer.markWallDirty();
    const position = `${next + 1} of ${ordered.length}`;
    this.setStatus(
      `${BRICK_SPECS[brick.kind].name} at X ${formatMm(brick.x)}, Y ${formatMm(brick.y)} mm · ${position}`,
    );
    // Bring it into view if it is off screen.
    const view = this.#renderer.viewport.visibleBounds(-30);
    if (brick.x < view.minX || brick.right > view.maxX || brick.y < view.minY || brick.top > view.maxY) {
      this.#renderer.viewport.panBy(
        this.#renderer.viewport.toScreenX((view.minX + view.maxX) / 2) - this.#renderer.viewport.toScreenX(brick.centreX),
        this.#renderer.viewport.toScreenY((view.minY + view.maxY) / 2) - this.#renderer.viewport.toScreenY(brick.y + brick.height / 2),
      );
      this.#renderer.markWallDirty();
    }
    return true;
  }

  /** Select one brick by id, used by the accessible wall outline. */
  focusBrick(id: BrickId): void {
    const brick = this.#model.get(id);
    if (brick === undefined) return;
    this.#state.setSelected(new Set([id]));
    this.#renderer.viewport.fit({
      minX: brick.x - 400,
      minY: brick.y - 300,
      maxX: brick.right + 400,
      maxY: brick.top + 300,
    });
    this.#renderer.markWallDirty();
    this.setStatus(
      `${BRICK_SPECS[brick.kind].name} at X ${formatMm(brick.x)}, Y ${formatMm(brick.y)} mm`,
    );
  }

  // ---------- bond generator (design document C4) ----------

  /** Sketch draw modes: a free polyline, or a dragged rectangle. */
  get drawMode(): SketchDrawMode {
    return this.#drawMode;
  }

  setDrawMode(mode: SketchDrawMode): void {
    this.#drawMode = mode;
    this.#draftPoints = [];
    this.#draftCursor = null;
    this.#onSketchChange?.();
    this.#renderer.markOverlayDirty();
  }

  /**
   * Dragging a rectangle is simply the quickest way to draw a closed shape —
   * the same concept as a hand-drawn outline, not a parallel one (F19).
   */
  #updateRectangle(point: { x: number; y: number }): void {
    const press = this.#rectPress;
    if (press === null) return;
    const a = this.#sketchSnap(press);
    const b = this.#sketchSnap(point);
    if (Math.abs(a.x - b.x) < 1 || Math.abs(a.y - b.y) < 1) {
      this.#draftPoints = [];
    } else {
      const minX = Math.min(a.x, b.x);
      const maxX = Math.max(a.x, b.x);
      const minY = Math.min(a.y, b.y);
      const maxY = Math.max(a.y, b.y);
      this.#draftPoints = [
        { x: minX, y: minY },
        { x: maxX, y: minY },
        { x: maxX, y: maxY },
        { x: minX, y: maxY },
      ];
      this.#draftCursor = null;
    }
    this.#renderer.markOverlayDirty();
    this.#onSketchChange?.();
  }

  get bondRequest(): Omit<BondRequest, 'envelope' | 'perpendGapMm'> {
    return this.#bondRequest;
  }

  setBondRequest(request: Omit<BondRequest, 'envelope' | 'perpendGapMm'>): void {
    this.#bondRequest = request;
    this.#onSketchChange?.();
  }

  #commitSelection(point: { x: number; y: number }): void {
    const state = this.#state;
    const model = this.#model;
    const viewport = this.#renderer.viewport;
    let hits: Set<BrickId>;
    if (this.#marqueeMoved && this.#marqueePress !== null) {
      const a = this.#marqueePress;
      hits = new Set(
        model
          .bricksInRect(
            viewport.toWorldX(a.x),
            viewport.toWorldY(a.y),
            viewport.toWorldX(point.x),
            viewport.toWorldY(point.y),
          )
          .map((brick) => brick.id),
      );
    } else {
      const brick = model.hitTest(viewport.toWorldX(point.x), viewport.toWorldY(point.y));
      hits = brick === undefined ? new Set() : new Set([brick.id]);
    }

    let next: Set<BrickId>;
    if (this.#marqueeModifier === 'add') {
      next = new Set([...this.#marqueeBase, ...hits]);
    } else if (this.#marqueeModifier === 'toggle') {
      next = new Set(this.#marqueeBase);
      for (const id of hits) {
        if (next.has(id)) next.delete(id);
        else next.add(id);
      }
    } else {
      next = hits;
    }
    state.setSelected(next);
    this.setStatus(next.size > 0 ? COPY.selected(next.size) : COPY.selectionCleared);
    this.#renderer.markWallDirty();
  }

  // ---------- commands the shell calls ----------

  /**
   * Abandon any gesture in flight. Called when the tool changes: pressing a
   * tool key mid-drag used to commit the move anyway, because nothing reset
   * the gesture (F2), and an unfinished sketch run was left stranded and
   * invisible (F7).
   */
  cancelGesture(): void {
    const hadMove = this.#gesture === 'move' && this.#moveStarted;
    this.#vertexDrag = null;
    this.#vertexPress = null;
    this.#vertexMoved = false;
    this.#rectPress = null;
    if (this.#draftPoints.length > 0) {
      // The run is kept as guide lines, which is what Escape already does.
      this.finishSketchLine();
    }
    this.#gesture = 'none';
    this.#panLast = null;
    this.#resetMove();
    this.#marqueePress = null;
    this.#marqueeCurrent = null;
    this.#marqueeMoved = false;
    this.#cancelLongPress();
    this.#touches.clear();
    this.#pinch = null;
    this.#touchStart = null;
    if (hadMove) this.setStatus(COPY.moveCancelled, true);
    this.#renderer.markWallDirty();
  }

  setSpaceHeld(held: boolean): void {
    this.#spaceHeld = held;
  }

  zoom(factor: number): void {
    if (this.#renderer.viewport.zoomBy(factor)) {
      this.#renderer.markWallDirty();
      this.refreshPreview();
    }
  }

  zoomIn(): void {
    this.zoom(BUTTON_STEP);
  }

  zoomOut(): void {
    this.zoom(1 / BUTTON_STEP);
  }

  fit(): void {
    this.#renderer.viewport.fit(this.#model.bounds());
    this.#renderer.markWallDirty();
    this.refreshPreview();
  }

  deleteSelection(): void {
    const state = this.#state;
    const model = this.#model;
    const ids = [...state.selected].filter((id) => model.get(id) !== undefined);
    if (ids.length === 0) {
      // Nothing built is selected, but something drawn may be. Bricks first,
      // because a selection of bricks is the one Delete has always meant.
      if (this.deleteSketchSelection()) return;
      this.setStatus(COPY.nothingToDelete, true);
      return;
    }
    this.#mutate(() => model.removeMany(ids) > 0);
    state.setSelected(new Set());
    this.clearMeasurement();
    this.setStatus(COPY.deleted(ids.length), true);
    this.refreshPreview();
  }

  clearWall(): void {
    const model = this.#model;
    if (model.size === 0) return;
    this.#mutate(() => model.clear());
    this.#state.setSelected(new Set());
    this.clearMeasurement();
    this.setStatus(COPY.cleared, true);
    this.refreshPreview();
  }

  selectAll(): void {
    const state = this.#state;
    state.setTool('select');
    const ids = new Set(this.#model.bricks.map((brick) => brick.id));
    state.setSelected(ids);
    this.setStatus(COPY.selectedAll(ids.size));
    this.#renderer.markWallDirty();
  }

  copySelection(): boolean {
    const state = this.#state;
    if (state.selected.size === 0) {
      this.setStatus(COPY.nothingToCopy, true);
      return false;
    }
    try {
      const pattern = this.#model.patternFromIds(state.selected);
      state.setClipboard(pattern);
      this.setStatus(COPY.copied(pattern.bricks.length));
      return true;
    } catch (error) {
      this.setStatus(error instanceof BrickwallError ? error.message : String(error), true);
      return false;
    }
  }

  beginPaste(): boolean {
    const state = this.#state;
    if (state.clipboard === null) {
      this.setStatus(COPY.nothingToPaste, true);
      return false;
    }
    state.setTool('paste');
    this.clearMeasurement();
    this.refreshPreview();
    this.setStatus(
      `Paste pattern: move ${plural(state.clipboard.bricks.length)}, then click repeatedly. Esc finishes.`,
    );
    return true;
  }

  cancelInteraction(): void {
    const state = this.#state;
    if (state.tool === 'sketch' && this.#draftPoints.length > 0) {
      this.finishSketchLine();
      return;
    }
    if (this.#gesture === 'move' && this.#moveStarted) {
      this.#gesture = 'none';
      this.#resetMove();
      this.#renderer.markWallDirty();
      this.setStatus(COPY.moveCancelled, true);
      return;
    }
    const wasPasting = state.tool === 'paste';
    state.setTool('select');
    this.clearMeasurement();
    this.refreshPreview();
    if (wasPasting) this.setStatus(COPY.pasteCancelled, true);
    else this.restStatus();
  }

  undo(): void {
    const state = this.#state;
    if (!state.history.undo(state.project)) return;
    this.#afterHistory();
    this.setStatus(COPY.undone, true);
  }

  redo(): void {
    const state = this.#state;
    if (!state.history.redo(state.project)) return;
    this.#afterHistory();
    this.setStatus(COPY.redone, true);
  }

  #afterHistory(): void {
    const state = this.#state;
    const model = this.#model;
    const live = new Set(model.bricks.map((brick) => brick.id));
    state.setSelected(new Set([...state.selected].filter((id) => live.has(id))));
    state.touch();
    this.clearMeasurement();
    this.#renderer.markWallDirty();
    this.refreshPreview();
  }

  /** Called after any external change to the document (load, joints, walls). */
  afterDocumentChange(fit = false): void {
    this.clearMeasurement();
    this.#invalidateBuilding();
    if (fit) this.#renderer.viewport.fit(this.#model.bounds());
    this.#renderer.markWallDirty();
    this.refreshPreview();
  }

  /** Repaint after a change that is only visual — no document mutation. */
  redraw(): void {
    this.#renderer.markWallDirty();
  }

  checkpoint(): void {
    const state = this.#state;
    state.history.checkpoint(state.project);
  }

  // ---------- plan view (design: phase 2 plan, M5) ----------

  /** The assembled building, cached against the document revision. */
  building(): BuildingModel {
    const state = this.#state;
    if (this.#building === null || this.#buildingRevision !== state.project.revision) {
      this.#building = assembleBuilding(state.project);
      this.#buildingRevision = state.project.revision;
    }
    return this.#building;
  }

  /** Drop the cache after a mutation, so the next read reassembles. */
  #invalidateBuilding(): void {
    this.#building = null;
    this.#buildingRevision = -1;
  }

  #planPoint(point: { x: number; y: number }): Vec2 {
    return screenToPlan(this.#renderer.viewport, point.x, point.y);
  }

  planWall(id: string | null): PlacedWall | null {
    if (id === null) return null;
    return this.building().walls.find((wall) => wall.id === id) ?? null;
  }

  /**
   * Switching into or out of the plan keeps each view's own pan and zoom.
   *
   * Sharing one viewport would drop the elevation somewhere arbitrary every
   * time you glanced at the plan, and vice versa. The first visit to the plan
   * fits the building, which is almost always what is wanted.
   */
  syncViewForTool(previous: string, next: string): void {
    if (previous === next) return;
    const viewport = this.#renderer.viewport;
    if (next === 'plan') {
      this.#elevationView = viewport.state;
      if (this.#planView === null) this.fitPlan();
      else viewport.state = this.#planView;
    } else if (previous === 'plan') {
      this.#planView = viewport.state;
      this.#planDrag = null;
      this.#planPlacement = null;
      this.#planMarks = [];
      if (this.#elevationView !== null) viewport.state = this.#elevationView;
    }
    this.#renderer.markWallDirty();
  }

  /** Frame the whole building in the plan view. */
  fitPlan(): void {
    const bounds = this.building().bounds;
    this.#renderer.viewport.fit(
      bounds === null
        ? null
        : {
            // The plan plots z downward as -y, so the world z range maps to a
            // y range with its sign flipped.
            minX: bounds.min.x,
            maxX: bounds.max.x,
            minY: -bounds.max.z,
            maxY: -bounds.min.z,
          },
    );
    this.#renderer.markWallDirty();
  }

  #planPointerDown(point: { x: number; y: number }, event: PointerEvent): void {
    const state = this.#state;
    const selected = this.planWall(state.project.activeWallId);

    // The rotate grip is drawn outside the wall, so it has to be tested first
    // or it could never be grabbed at all.
    if (selected !== null) {
      const handle = rotateHandleAt(this.#renderer.viewport, selected);
      if (handle !== null && Math.hypot(handle.x - point.x, handle.y - point.y) <= 12) {
        const pivot = this.#pivotOf(selected);
        this.#gesture = 'plan-rotate';
        this.#planDrag = {
          wallId: selected.id,
          press: this.#planPoint(point),
          startPlacement: selected.placement,
          grabAngleDeg: planAngleDeg(pivot, this.#planPoint(point)),
        };
        this.#planPlacement = selected.placement;
        this.#planMoved = false;
        this.setStatus(COPY.planRotating(selected.name), false, false);
        return;
      }
    }

    const at = this.#planPoint(point);
    const hit =
      selected !== null && hitWall(selected, at) ? selected : wallAt(this.building(), at);
    // Clicking bare ground no longer clears the selection: there is exactly
    // one active wall now, and dropping it would leave the walls list, the
    // step bar and the placement fields with nothing to point at.
    if (hit === null) {
      this.restStatus();
      return;
    }

    if (hit.id !== state.project.activeWallId) {
      state.project.setActiveWall(hit.id);
      // A selection of bricks belongs to the wall it was made in.
      state.setSelected(new Set());
      state.touch();
      this.setStatus(COPY.planSelected(hit.name), false, false);
    }
    this.#gesture = 'plan-move';
    this.#planDrag = {
      wallId: hit.id,
      press: at,
      startPlacement: hit.placement,
      grabAngleDeg: 0,
    };
    this.#planPlacement = hit.placement;
    this.#planMarks = [];
    this.#planMoved = false;
    this.#renderer.markWallDirty();
    void event;
  }

  #pivotOf(wall: PlacedWall): Vec2 {
    return wall.centreline === null
      ? { x: wall.placement.origin.x, z: wall.placement.origin.z }
      : wall.centreline[0];
  }

  #planPointerMove(point: { x: number; y: number }, event: PointerEvent): void {
    const drag = this.#planDrag;
    if (drag === null) return;
    const wall = this.planWall(drag.wallId);
    if (wall === null) return;
    const at = this.#planPoint(point);
    // Alt is the bypass everywhere else in the app, so it is the bypass here.
    const bypass = event.altKey || !this.#state.snapEnabled;

    if (this.#gesture === 'plan-rotate') {
      const pivot = this.#pivotOf(wall);
      const swept = planAngleDeg(pivot, at) - drag.grabAngleDeg;
      // A wall at rotation r runs along (cos r, -sin r), so plan angle is -r.
      this.#planPlacement = rotateWall(
        { ...wall, placement: drag.startPlacement },
        drag.startPlacement.rotationDeg - swept,
        !bypass,
      );
      this.#planMarks = [];
      this.#planMoved = true;
      this.#renderer.markWallDirty();
      this.#publishPlanReadout();
      return;
    }

    const snap = moveWall(
      { ...wall, placement: drag.startPlacement },
      at.x - drag.press.x,
      at.z - drag.press.z,
      bypass ? [] : otherEnds(this.building(), wall.id),
      bypass ? 0 : this.#renderer.viewport.snapToleranceMm,
    );
    this.#planPlacement = snap.placement;
    this.#planMarks = snap.marks;
    this.#planMoved = true;
    this.#renderer.markWallDirty();
    this.#publishPlanReadout();
  }

  #planPointerUp(): void {
    const drag = this.#planDrag;
    const placement = this.#planPlacement;
    this.#planDrag = null;
    this.#planPlacement = null;
    this.#planMarks = [];
    this.#gesture = 'none';
    if (drag === null || placement === null || !this.#planMoved) {
      // A click that selected without moving: no undo step, but the status
      // should now name what is selected.
      this.#renderer.markWallDirty();
      this.restStatus();
      return;
    }
    this.#planMoved = false;
    const state = this.#state;
    this.#mutate(() => state.project.setWallPlacement(drag.wallId, placement));
    this.#invalidateBuilding();
    const wall = this.planWall(drag.wallId);
    if (wall !== null) this.setStatus(COPY.planPlaced(wall.name, placement), true);
    this.#renderer.markWallDirty();
  }

  /**
   * Nudge the selected wall with the arrow keys, and rotate it with [ and ].
   * The plan has to be operable without a mouse like everything else (C8).
   */
  nudgePlan(dx: number, dz: number, rotateBy: number): boolean {
    const state = this.#state;
    const wall = this.planWall(state.project.activeWallId);
    if (wall === null) return false;
    const placement =
      rotateBy === 0
        ? {
            ...wall.placement,
            origin: {
              x: wall.placement.origin.x + dx,
              y: wall.placement.origin.y,
              z: wall.placement.origin.z + dz,
            },
          }
        : rotateWall(wall, wall.placement.rotationDeg + rotateBy, false);
    this.#mutate(() => state.project.setWallPlacement(wall.id, placement));
    this.#invalidateBuilding();
    this.setStatus(COPY.planPlaced(wall.name, placement), true);
    this.#renderer.markWallDirty();
    return true;
  }

  /** Set the thickness of the selected wall, in millimetres. */
  setPlanThickness(thicknessMm: number): boolean {
    const state = this.#state;
    const wall = this.planWall(state.project.activeWallId);
    if (wall === null || !Number.isFinite(thicknessMm) || thicknessMm <= 0) return false;
    this.#mutate(() =>
      state.project.setWallPlacement(wall.id, { ...wall.placement, thicknessMm }),
    );
    this.#invalidateBuilding();
    this.#renderer.markWallDirty();
    return true;
  }

  /** Roof settings, from the plan panel (phase 2, M7). */
  setRoof(patch: Partial<RoofSpec>): boolean {
    const state = this.#state;
    this.#mutate(() => state.project.setRoof(patch));
    this.#invalidateBuilding();
    this.#renderer.markWallDirty();
    return true;
  }

  /** Defaults for generated windows and doors (phase 2, M7). */
  setJoinery(patch: Partial<JoinerySpec>): boolean {
    const state = this.#state;
    this.#mutate(() => state.project.setJoinery(patch));
    this.#invalidateBuilding();
    return true;
  }

  /** Write a placement directly, from the numeric fields in the panel. */
  setPlanPlacement(placement: WallPlacement): boolean {
    const state = this.#state;
    const wall = this.planWall(state.project.activeWallId);
    if (wall === null) return false;
    this.#mutate(() => state.project.setWallPlacement(wall.id, placement));
    this.#invalidateBuilding();
    this.#renderer.markWallDirty();
    return true;
  }

  /** The dragged wall's rectangle at its candidate placement, for the ghost. */
  #planGhostCorners(): readonly Vec2[] | undefined {
    const drag = this.#planDrag;
    const placement = this.#planPlacement;
    if (drag === null || placement === null || !this.#planMoved) return undefined;
    const wall = this.planWall(drag.wallId);
    if (wall === null || wall.extent === null) return undefined;
    const t = placement.thicknessMm;
    return [
      wallToPlan(placement, wall.extent.minX, 0),
      wallToPlan(placement, wall.extent.maxX, 0),
      wallToPlan(placement, wall.extent.maxX, t),
      wallToPlan(placement, wall.extent.minX, t),
    ];
  }

  #publishPlanReadout(): void {
    const placement = this.#planPlacement;
    if (placement === null) return;
    setVolatile({
      coordinate: `X ${formatMm(placement.origin.x)}  Z ${formatMm(placement.origin.z)}  ${formatMm(
        placement.rotationDeg,
      )}°`,
    });
  }

  // ---------- scene ----------

  scene(): Scene {
    const state = this.#state;
    const project = state.project;
    const model = this.#model;
    const colourOfMaterial = (materialId: string): string =>
      project.material(materialId)?.colour ?? project.materials[0]?.colour ?? '#B5523B';

    const scene: {
      -readonly [K in keyof Scene]: Scene[K];
    } = {
      model,
      colourOf: (brick) => colourOfMaterial(brick.materialId),
      colourOfMaterial,
      selected: state.selected,
      emptyHint: state.welcomeDismissed,
    };

    // The plan is a different view of the same document, so it replaces the
    // elevation on the wall layer rather than adding an overlay to it.
    if (state.tool === 'plan') {
      const scene2: typeof scene = scene;
      scene2.plan = {
        building: this.building(),
        activeWallId: project.activeWallId,
        colourOfMaterial,
        ghost: this.#planGhostCorners(),
        snapMarks: this.#planMarks,
      };
      return scene;
    }

    if (state.tool === 'build' && this.#build !== null) {
      const [width, height] = [this.#build.width, this.#build.height];
      scene.build = {
        snap: this.#build.snap,
        width,
        height,
        valid: this.#build.valid,
        caption: this.#build.valid
          ? `${BRICK_SPECS[state.selectedKind].name}  ${formatMm(width)} × ${formatMm(height)}`
          : `Needs ${formatMm(model.perpendGapMm)} mm perpend / ${formatMm(model.bedGapMm)} mm bed clearance`,
      };
    }
    if (state.tool === 'paste' && this.#paste !== null && state.clipboard !== null) {
      scene.paste = {
        snap: this.#paste.snap,
        pattern: state.clipboard,
        valid: this.#paste.valid,
        caption: this.#paste.valid
          ? `Pattern · ${plural(state.clipboard.bricks.length)}`
          : `Pattern needs ${formatMm(model.perpendGapMm)} mm perpend / ${formatMm(model.bedGapMm)} mm bed clearance`,
      };
    }
    // A move draws exactly like a paste: ghosts at the candidate position with
    // the originals dimmed in place.
    if (this.#moveStarted && this.#moveOffset !== null && this.#moveIds.size > 0) {
      const bounds = this.#moveBounds;
      const pattern = this.#movePattern;
      if (bounds !== null && pattern !== null) {
        {
          scene.dimmed = this.#moveIds;
          scene.paste = {
            snap: {
              x: bounds.minX + this.#moveOffset.dx,
              y: bounds.minY + this.#moveOffset.dy,
              relation: this.#moveRelation,
              referenceId: null,
              snappedX: this.#moveRelation !== null,
              snappedY: this.#moveRelation !== null,
            },
            pattern,
            valid: this.#moveValid,
            caption: this.#moveValid
              ? `Move · ${plural(this.#moveIds.size)}`
              : `Needs ${formatMm(model.perpendGapMm)} mm perpend / ${formatMm(model.bedGapMm)} mm bed clearance`,
          };
        }
      }
    }

    if (!isEmptySketch(model.sketch) || this.#draftPoints.length > 0 || this.#snapHint !== null) {
      scene.sketch = {
        sketch: model.sketch,
        selection: this.#sketchTarget,
        // Guarded by the tool rather than only cleared when the pointer next
        // moves: a tool switched from the keyboard leaves the pointer where it
        // was, and a hover highlight that outlives its tool is a lie.
        hover: state.tool !== 'sketch' || this.#sketchHover === null
          ? null
          : this.#sketchHover.kind === 'vertex'
            ? { kind: 'vertex' as const, point: this.#sketchHover.vertex.point }
            : {
                kind: 'line' as const,
                target: this.#sketchHover.target,
                line: this.#sketchHover.line,
              },
        snap: state.tool === 'sketch' ? this.#snapHint : null,
        dragging: this.#vertexDrag,
        moving:
          this.#sketchMove !== null && this.#sketchMove.moved
            ? {
                target: this.#sketchMove.target,
                dx: this.#sketchMove.dx,
                dy: this.#sketchMove.dy,
              }
            : null,
        draft:
          state.tool === 'sketch' && this.#draftPoints.length > 0
            ? {
                points: this.#draftPoints,
                cursor: this.#draftCursor,
                kind: this.#lineKind,
                closing: this.#draftClosing,
              }
            : undefined,
        hoverPoint: this.#vertexDrag?.point ?? null,
      };
    }

    if (this.#keyboardCursor !== null) {
      scene.keyboardCursor = this.#keyboardCursor;
    }

    if (this.#marqueeMoved && this.#marqueePress !== null && this.#marqueeCurrent !== null) {
      scene.marquee = {
        x1: this.#marqueePress.x,
        y1: this.#marqueePress.y,
        x2: this.#marqueeCurrent.x,
        y2: this.#marqueeCurrent.y,
      };
    }
    if (state.tool === 'measure') {
      scene.measure = {
        measurement: this.#measurement,
        start: this.#measureStart,
        hover: this.#measureHover,
      };
    }
    return scene as Scene;
  }
}

function summariseMeasurement(measurement: Measurement): string {
  return (
    `X ${formatMm(measurement.xDistance)} mm · ` +
    `Y ${formatMm(measurement.yDistance)} mm · ` +
    `Absolute distance ${measurement.roundedAbsoluteDistance} mm · ` +
    `Angle ${measurement.roundedAngleFromHorizontal.toFixed(1)}° from horizontal`
  );
}

export { effectiveSize };
